import { useState, useEffect, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { ESTADOS_COTIZABLE } from "../../constants/estadosEdificio";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { actualizarActivo, getActivos } from "../../utils/activos";
import { getClientesActivos, crearClienteMinimal } from "../../utils/clientes";
import { parsearSwiftDesdePdf } from "../../utils/cobro/parseSwiftPdf";
import {
  desvincularComisionIngreso,
  getComisionesIngresoNeteadas,
  getFacturasComisionIngresoDisponibles,
  vincularComisionIngreso,
} from "../../utils/comisionesIngresoNeteadas";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { getCreditosConCuotasPendientes, vincularMovimientoACuota } from "../../utils/creditos";
import { getEmpleados } from "../../utils/empleados";
import { getSaldosPorFacturaProveedor } from "../../utils/estadoCuenta";
import { FECHA_CORTE } from "../../utils/estadoCuenta/saldoInicial";
import { convertirAMonedaLocal } from "../../utils/exchangeRateService";
import { actualizarOperacionFactoring, getOperacionesFactoring } from "../../utils/factoring";
import { crearMovimiento } from "../../utils/flujoCaja";
import { getEdificiosSaldoPendiente } from "../../utils/getEdificiosSaldoPendiente";
import { actualizarMatch, confirmarMovimiento, getFacturasYaVinculadas } from "../../utils/movimientosBancarios";
import {
  crearPagosEmpleado,
  eliminarPagosPorMovimiento,
  getEmpleadoIdsConFiniquito,
  TIPOS_PAGO_EMPLEADO,
  TIPOS_REQUIEREN_PERIODO,
} from "../../utils/pagosEmpleados";
import { getPlanCobros, getPlanPagos, actualizarCuotaCobro, actualizarCuotaPago } from "../../utils/planCobros";
import { getProveedores, crearProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import { FacturaExternaForm, useFacturaExternaForm } from "../FacturaExternaModal";
import ProveedorSelector from "./ProveedorSelector";
import "./VincularMovimientoModal.scss";

const ROLES_PAGO_EMPLEADO = [ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.SUPERADMIN];

// Tolerancias del cuadre al vincular facturas EXTERNAS a un movimiento bancario (gate `extCuadra`).
// Sensibles a la moneda:
// - Cross-currency convertible (banco CLP/PEN, factura USD): 15%. Absorbe el spread cambiario del
//   banco (convierte USD→CLP con SU propio TC, no el del sistema); el peor caso legítimo observado
//   fue 8.6% (Screenfeed marzo). Sigue frenando descuadres gruesos (mes equivocado, monto mal
//   cargado → >15%).
// - Same-currency (misma moneda): 1% — INTACTO. Misma moneda debe cuadrar casi exacto.
const TOLERANCIA_EXT_CROSS_CURRENCY = 0.15;
const TOLERANCIA_EXT_SAME_CURRENCY = 0.01;

// Path "Pago institucional": pagos a entidades sin DTE (PREVIRED, SII, banco, etc.).
// cuenta_er solo se setea para 'banco' — los pagos previsionales y tributarios son
// pasivos, no gastos operativos del Estado de Resultados.
const INSTITUCIONES = [
  {
    value: "previred",
    label: "PREVIRED",
    categoria_pago: "cotizaciones_previsionales",
    cuenta_er: null,
    requierePeriodo: true,
  },
  {
    value: "sii_declaracion",
    label: "SII — Declaración mensual",
    categoria_pago: "pago_impuesto",
    cuenta_er: null,
    requierePeriodo: true,
  },
  { value: "tesoreria", label: "Tesorería", categoria_pago: "pago_impuesto", cuenta_er: null, requierePeriodo: false },
  {
    value: "banco",
    label: "Banco — comisión/mantención",
    categoria_pago: "pago_banco",
    cuenta_er: "gastos_bancarios",
    requierePeriodo: false,
  },
  { value: "otro", label: "Otro", categoria_pago: "otro", cuenta_er: null, requierePeriodo: false },
];

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const parseFechaLocal = (str) => {
  if (!str) return new Date();
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const nuevaLineaComponente = (movimiento, tipoDefault = "sueldo") => {
  const fecha = parseFechaLocal(movimiento?.fecha);
  return {
    tipo: tipoDefault,
    periodo_año: fecha.getFullYear(),
    periodo_mes: fecha.getMonth() + 1,
    monto: "",
    notas: "",
  };
};

// Selector reutilizable de "comisión neteada" (factoring o transferencia recibida). Maneja su
// propia carga vía loaders inyectados; presentación tipo lista + chips vinculados con "×".
// destacar(f) → resalta/ordena primero (≈ comisión por monto, o fecha cercana). NO bloquea nada.
const ComisionNeteadaSelector = ({
  titulo,
  cargarVinculadas,
  cargarDisponibles,
  vincular,
  desvincular,
  destacar,
  formatAmount,
  formatFecha,
  moneda,
  onCambio,
  mostrarDteAnioAnterior,
  setMostrarDteAnioAnterior,
}) => {
  const [vinculadas, setVinculadas] = useState([]);
  const [disponibles, setDisponibles] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);
  // Fix 2: el click sobre una fila SELECCIONA (highlight); un botón explícito confirma el vínculo.
  const [facturaSelId, setFacturaSelId] = useState(null);

  const recargar = async () => {
    try {
      const [v, d] = await Promise.all([cargarVinculadas(), cargarDisponibles()]);
      setVinculadas(v || []);
      setDisponibles(d || []);
    } catch {
      // silencioso: el panel solo informa
    }
  };

  useEffect(() => {
    recargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVincular = async (facturaId) => {
    setLoading(true);
    try {
      await vincular(facturaId);
      await recargar();
      setFacturaSelId(null);
      toast.success("Comisión vinculada ✓");
      onCambio?.(); // refresca "Por pagar" en el padre (FlujoCajaV2)
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleDesvincular = async (filaId) => {
    setLoading(true);
    try {
      await desvincular(filaId);
      await recargar();
      toast.success("Comisión desvinculada");
      onCambio?.();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // añoActualPI vive en el modal padre y no está en scope acá → año actual local para el corte.
  const añoActualCN = new Date().getFullYear();
  const ordenadas = [...disponibles]
    // "Mostrar DTE AA" apagado (default): ocultar DTEs de años anteriores al actual (sin fecha → conservar).
    .filter(
      (f) => mostrarDteAnioAnterior || !f.fecha_emision || parseInt(f.fecha_emision.substring(0, 4), 10) >= añoActualCN
    )
    .filter((f) => {
      if (!busqueda.trim()) return true;
      const q = busqueda.trim().toLowerCase();
      return (
        String(f.folio).includes(q) || String(Math.round(Number(f.monto_total) || 0)).includes(q.replace(/\D/g, ""))
      );
    })
    .sort((a, b) => {
      const sa = destacar?.(a) ? 0 : 1;
      const sb = destacar?.(b) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return (b.fecha_emision || "").localeCompare(a.fecha_emision || "");
    });

  return (
    <div style={{ marginTop: 12, borderTop: "1px solid #e6dcc0", paddingTop: 10 }}>
      <div style={{ fontSize: "0.8rem", fontWeight: 700, marginBottom: 6 }}>{titulo}</div>

      {vinculadas.length > 0 ? (
        <div style={{ marginBottom: 8 }}>
          {vinculadas.map((c) => (
            <div
              key={c.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "4px 10px",
                marginBottom: 3,
                borderRadius: 4,
                background: "#f0f9f4",
                border: "1px solid #198754",
                fontSize: "0.78rem",
              }}
            >
              <span style={{ color: "#198754", fontWeight: 600 }}>
                ✓ Folio {c.factura?.folio ?? c.factura_sii_recibida_id}
                {c.factura?.monto_total != null ? ` · ${formatAmount(c.factura.monto_total, moneda)}` : ""}
              </span>
              <button
                type="button"
                onClick={() => handleDesvincular(c.id)}
                disabled={loading}
                style={{
                  border: "1px solid #c62828",
                  background: "none",
                  borderRadius: 4,
                  color: "#c62828",
                  fontSize: "0.72rem",
                  padding: "1px 7px",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: "0.78rem", color: "#c0392b", fontStyle: "italic", marginBottom: 6 }}>pendiente</div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Buscar folio o monto..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ flex: 3, fontSize: "0.8rem" }}
        />
        <label
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            whiteSpace: "nowrap",
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            checked={mostrarDteAnioAnterior}
            onChange={(e) => setMostrarDteAnioAnterior(e.target.checked)}
          />
          Mostrar DTE AA
        </label>
      </div>
      <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
        {ordenadas.length === 0 ? (
          <div style={{ padding: 10, textAlign: "center", color: "#999", fontSize: "0.76rem" }}>
            Sin facturas disponibles
          </div>
        ) : (
          ordenadas.map((f) => {
            const sug = !!destacar?.(f);
            const sel = facturaSelId === f.id;
            return (
              <div
                key={f.id}
                onClick={() => !loading && setFacturaSelId(sel ? null : f.id)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "5px 10px",
                  borderBottom: "1px solid #f0f0f0",
                  fontSize: "0.76rem",
                  cursor: loading ? "not-allowed" : "pointer",
                  background: sel ? "#e8f4fd" : sug ? "#f0f9f4" : "transparent",
                  fontWeight: sel ? 600 : 400,
                }}
              >
                <span>
                  {sel ? "✓ " : ""}
                  {formatFecha(f.fecha_emision)} · Folio {f.folio}
                  {sug && (
                    <span style={{ marginLeft: 5, color: "#198754", fontSize: "0.68rem", fontWeight: 600 }}>
                      ✓ sugerida
                    </span>
                  )}
                </span>
                <span style={{ fontWeight: 600, color: "#c0392b" }}>{formatAmount(f.monto_total, moneda)}</span>
              </div>
            );
          })
        )}
      </div>

      {facturaSelId != null && (
        <button
          type="button"
          onClick={() => handleVincular(facturaSelId)}
          disabled={loading}
          style={{
            marginTop: 8,
            width: "100%",
            padding: "6px 0",
            border: "none",
            borderRadius: 4,
            background: "#198754",
            color: "#fff",
            fontSize: "0.8rem",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Vinculando..." : "Vincular comisión"}
        </button>
      )}
    </div>
  );
};

const VincularMovimientoModal = ({ show, onClose, movimiento, onSuccess, onRefreshFuturos }) => {
  const { pais, rol, id: usuarioId } = useAuthContext();
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";
  const { formatAmount } = useCurrency();
  const puedePagarEmpleado = ROLES_PAGO_EMPLEADO.includes(rol);

  const [tipoVinculacion, setTipoVinculacion] = useState(movimiento?.tipo === "egreso" ? "pago" : "cobro");
  const [facturas, setFacturas] = useState([]);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  // Selección múltiple. Cuando length===1, contiene la misma que facturaSeleccionada
  // (single mode preservado). Cuando length>=2, activa modo múltiple (sin cuotas, mismo
  // proveedor/cliente, suma exacta). Persistencia: tabla movimiento_bancario_facturas.
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  const [cuotas, setCuotas] = useState([]);
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState(null);
  // Propina de restorán (sin DTE): el usuario marca que el exceso del débito sobre la
  // factura SII es propina. Misma forma que los Gastos OUR (regla 10.79) pero independiente
  // y activado a mano. Se resetea al cambiar de factura o de movimiento.
  const [esPropina, setEsPropina] = useState(false);
  // Path "Préstamo": créditos activos con cuotas pendientes + selección actual.
  const [creditosDisponibles, setCreditosDisponibles] = useState([]);
  const [creditoPrestamo, setCreditoPrestamo] = useState(null);
  const [cuotaPrestamo, setCuotaPrestamo] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);
  // Checkbox "Mostrar DTE AA": apagado por defecto oculta de TODOS los listados de facturas los
  // DTEs cuyo año de fecha_emision sea menor al año actual (corte que se mueve solo con el año).
  // Estado único a nivel del modal, replicado en los 3 rows de buscador (SII, externas, comisión).
  const [mostrarDteAnioAnterior, setMostrarDteAnioAnterior] = useState(false);

  // Modo "Factura externa": "existente" (lista de pendientes con multi-select) o "nueva" (form de carga).
  const [facturasExtPendientes, setFacturasExtPendientes] = useState([]);
  // Saldo FIFO pendiente por factura (Map factura_id → saldoDisplay), del helper canónico. Solo
  // incluye facturas con saldo > 0.01 (las totalmente amortizadas por pagos a cuenta quedan fuera).
  const [saldosExtMap, setSaldosExtMap] = useState(() => new Map());
  const [externasSeleccionadas, setExternasSeleccionadas] = useState([]);
  const [busquedaExt, setBusquedaExt] = useState("");
  const [modoExterno, setModoExterno] = useState("existente");

  const [empleados, setEmpleados] = useState([]);
  const [empleadoId, setEmpleadoId] = useState(null);
  const [empleadoBusqueda, setEmpleadoBusqueda] = useState("");
  const [componentes, setComponentes] = useState([]);

  // Sub-modo en "Pago a empleado": "remuneracion" (sueldo/adelanto/bono/etc.) o "caja_chica"
  // (entrega de fondos). Caja Chica vive acá como sub-modo, no como tab de nivel 1.
  const [modoEmpleado, setModoEmpleado] = useState("remuneracion");
  // Modo de compra en "Pago a proveedor": "sii" (factura SII) o "externa" (doc manual extranjero)
  const [modoCompra, setModoCompra] = useState("sii");
  // Modo en "Cobro de cliente": "sii" (factura SII emitida) o "a_cuenta" (cobro a cuenta corriente del proveedor)
  const [modoCobro, setModoCobro] = useState("sii");
  const [proveedores, setProveedores] = useState([]);

  // Selector inline de entidad cuando la factura SII no tiene cliente_id (cobro) o proveedor_id (pago).
  // Sin esta entidad no se puede crear el asiento en movimientos_caja (Opción A).
  const [entidadFallbackOptions, setEntidadFallbackOptions] = useState([]);
  const [entidadFallbackId, setEntidadFallbackId] = useState(null);
  const [entidadFallbackBusqueda, setEntidadFallbackBusqueda] = useState("");

  // Modo "Sin factura": gastos operativos legítimos sin DTE (café con cliente, taxi, propinas).
  const [sfConcepto, setSfConcepto] = useState("");
  const [sfCuentaER, setSfCuentaER] = useState("");
  const [sfComercio, setSfComercio] = useState("");
  const [sfProveedorId, setSfProveedorId] = useState(null);
  const [sfProveedorNombre, setSfProveedorNombre] = useState("");
  const [acProveedorId, setAcProveedorId] = useState(null);
  const [acProveedorNombre, setAcProveedorNombre] = useState("");
  const [acNota, setAcNota] = useState("");
  const [acBucket, setAcBucket] = useState(""); // 'factura' | 'credito'
  // Path "A cuenta" en cobro: cobro a cuenta corriente del proveedor (devolución / anticipo retornado)
  const [acCobroProveedorId, setAcCobroProveedorId] = useState(null);
  const [acCobroProveedorNombre, setAcCobroProveedorNombre] = useState("");
  const [acCobroNota, setAcCobroNota] = useState("");
  const [acCobroBucket, setAcCobroBucket] = useState(""); // 'factura' | 'credito'

  // Cobro de exportación — "Costos del giro" (modelo 7.18). Franco copia del SWIFT el monto
  // instruido (InstdAmt) y la comisión del banco internacional (Citibank); el resto se deriva.
  // Campos opcionales del SWIFT (colapsables) para trazabilidad / dedup futura.
  const [giroInstdAmt, setGiroInstdAmt] = useState("");
  const [giroCitibank, setGiroCitibank] = useState("");
  const [giroSantanderRecibido, setGiroSantanderRecibido] = useState("");
  const [giroSwiftOpen, setGiroSwiftOpen] = useState(false);
  // Wizard del cobro de exportación: 3 pasos (1 Facturas · 2 Costos del giro · 3 Comisión Santander).
  // Solo aplica en modo export; el cobro normal queda en una sola pantalla.
  const [pasoExport, setPasoExport] = useState(1);
  const [cargandoSwift, setCargandoSwift] = useState(false);
  const [giroUetr, setGiroUetr] = useState("");
  const [giroOrdenante, setGiroOrdenante] = useState("Place Exchange Inc");
  const [giroOrdenanteBic, setGiroOrdenanteBic] = useState("");
  const [giroFechaValor, setGiroFechaValor] = useState("");

  // Crear proveedor desde el selector inline del path Pago SII cuando el RUT no existe en la tabla
  // proveedores (mismo patrón que "+ Nuevo proveedor" del path Factura externa).
  const [creandoProvSII, setCreandoProvSII] = useState(false);
  const [provSIINombre, setProvSIINombre] = useState("");
  const [provSIIRut, setProvSIIRut] = useState("");

  // Crear cliente desde el selector inline del path Cobro SII (simétrico al "+ Nuevo proveedor").
  const [creandoClienteSII, setCreandoClienteSII] = useState(false);
  const [clienteSIIEmpresa, setClienteSIIEmpresa] = useState("");
  const [clienteSIIRut, setClienteSIIRut] = useState("");

  // Modo "Pago institucional": PREVIRED, SII, Tesorería, banco, otros.
  const añoActualPI = new Date().getFullYear();
  const añosPI = [añoActualPI - 1, añoActualPI, añoActualPI + 1];
  const [piInstitucion, setPiInstitucion] = useState("");
  const [piPeriodoMes, setPiPeriodoMes] = useState(null);
  const [piPeriodoAño, setPiPeriodoAño] = useState(añoActualPI);
  const [piNotas, setPiNotas] = useState("");

  // Modo "Pago a edificio": pago de comisión a uno o varios edificios. Multi-edificio con
  // cuadre EXACTO: Σ montos asignados === abs(monto del movimiento). FASE 1 = solo UI/cuadre,
  // sin escritura a movimientos_caja (eso llega en Fase 2).
  // referencia: [{edificioId, edificioNombre, saldoPendiente, moneda}]
  const [edificiosSaldo, setEdificiosSaldo] = useState([]);
  // asignaciones tipeadas: [{edificioId, edificioNombre, proveedorId, monto}]
  const [edificiosAsignaciones, setEdificiosAsignaciones] = useState([]);
  const [edificioSelectorId, setEdificioSelectorId] = useState(""); // edificio elegido en el dropdown antes de "Agregar"
  // Acordeón "Adjuntar factura de soporte (opcional)" en modo edificio: cerrado por defecto.
  // La factura nunca es requisito; la lista solo se renderiza si el usuario abre esto.
  const [mostrarFacturaEdificio, setMostrarFacturaEdificio] = useState(false);

  // Path "Compra de activo" (D.2): vincula este egreso a un activo planificado (edificio) →
  // el activo pasa a 'activo' con fecha_compra = fecha del movimiento y sale del CapEx.
  const [activosCapex, setActivosCapex] = useState([]); // planificados del país [{...activo, edificioNombre}]
  const [activoCapexSel, setActivoCapexSel] = useState(null); // id del activo elegido

  // Tab "Factoring" (solo ingresos): vincula este movimiento al adelanto o al excedente
  // de una operación de factoring activa. No crea movimientos_caja — la operación de
  // factoring ya tiene su propio asiento contable; sólo registra la FK del banco.
  const [operacionesFactoring, setOperacionesFactoring] = useState([]);
  const [factoringOpIds, setFactoringOpIds] = useState([]);
  const [factoringTipoVinc, setFactoringTipoVinc] = useState("giro");
  // Si este movimiento ya está vinculado a una operación de factoring, guardamos el objeto
  // de la op acá. Cuando es != null, el modal pasa a modo "ya vinculado" con opción de
  // desvincular en lugar de crear vinculación nueva.
  const [yaVinculadoFactoring, setYaVinculadoFactoring] = useState(null);

  // Filas existentes en movimiento_bancario_facturas para este movimiento. Si hay >=1,
  // el modal muestra una sección "Vinculación actual" con botón para desvincular cada
  // factura SII (single o múltiple).
  const [vinculacionesSII, setVinculacionesSII] = useState([]);

  // Pagos a empleado vinculados a este movimiento (filas en pagos_empleados con
  // movimiento_bancario_id = mov.id). Tercera vía de detección de "ya vinculado":
  // empleado no escribe en movimiento_bancario_facturas ni en movimientos_caja.
  const [pagosEmpleadoVinculados, setPagosEmpleadoVinculados] = useState([]);

  const monto = Number(movimiento?.monto || 0);
  const moneda = movimiento?.moneda || (pais === "Perú" ? "PEN" : "CLP");
  const esCobro = tipoVinculacion === "cobro";
  const esEmpleado = tipoVinculacion === "empleado";
  const esEgreso = movimiento?.tipo === "egreso";
  const esPagoExterna = tipoVinculacion === "pago" && modoCompra === "externa";
  const esPagoSinFactura = tipoVinculacion === "pago" && modoCompra === "sin_factura";
  // P3: un proveedor que emite factura (es_extranjero) NO puede cerrarse como "Sin factura" —
  // debe cargarse en "Factura externa". Comparación por id (regla 10.7). Los locales sin DTE (Uber,
  // cafés, taxis, propinas) son es_extranjero=false → siguen usando "Sin factura" normal.
  const sfProveedorEsExtranjero = !!proveedores.find((p) => p.id === Number(sfProveedorId))?.es_extranjero;
  const esPagoACuenta = tipoVinculacion === "pago" && modoCompra === "a_cuenta";
  const esCobroACuenta = tipoVinculacion === "cobro" && modoCobro === "a_cuenta";
  const esCobroExportacion = tipoVinculacion === "cobro" && modoCobro === "exportacion";
  const esFactoring = tipoVinculacion === "factoring";
  const esCajaChica = tipoVinculacion === "empleado" && modoEmpleado === "caja_chica";
  const esRemuneracion = tipoVinculacion === "empleado" && modoEmpleado === "remuneracion";

  // Form de Factura externa: estado + lógica viven en hook compartido (modo bancario porque
  // VincularMov siempre se abre con un movimiento). El padre lo dispara desde Modal.Footer.
  const facturaExt = useFacturaExternaForm({
    pais,
    moneda,
    movimiento,
    proveedores,
    onProveedorCreado: (nuevo) =>
      setProveedores((prev) => [...prev, nuevo].sort((a, b) => a.nombre.localeCompare(b.nombre))),
    onSuccess,
    onClose,
  });
  const esPagoInstitucional = tipoVinculacion === "institucional";
  const esPagoEdificio = tipoVinculacion === "edificio";
  const esPagoActivo = tipoVinculacion === "activo";
  const esPrestamo = tipoVinculacion === "prestamo";

  // Comisión por transferencia internacional (Santander): el débito CLP incluye los "Gastos OUR"
  // (cobro del banco corresponsal) que NO llevan IVA y NO se incluyen en el DTE → la factura SII
  // recibida es MENOR que el débito bancario. Es un sobrepago legítimo y esperado, no un error.
  // Cuando esto es true: (1) no se bloquea el sobrepago, (2) se muestra el desglose de Gastos OUR,
  // (3) mbf.monto = monto_total de la factura (no el débito) para no distorsionar el FIFO. Ver 10.67.
  const esComisionTransferenciaInternacional =
    /transferencia internacional/i.test(movimiento?.descripcion_raw || "") &&
    (movimiento?.tipo === "egreso" || Number(movimiento?.monto || 0) < 0) &&
    tipoVinculacion === "pago" &&
    modoCompra === "sii";
  // Panel de desglose Gastos OUR: solo cuando hay sobrepago real sobre la factura seleccionada.
  const mostrarDesgloseOUR =
    esComisionTransferenciaInternacional &&
    !!facturaSeleccionada &&
    Math.abs(monto) > Number(facturaSeleccionada?.monto_total || 0);
  // Guardrails visuales (NO bloquean el confirmar): advierten cuando la factura seleccionada
  // probablemente NO corresponde al débito. Solo aplican dentro del caso OUR con sobrepago.
  const gastosOUR = mostrarDesgloseOUR ? Math.abs(monto) - Number(facturaSeleccionada.monto_total || 0) : 0;
  const ourPorcentaje = mostrarDesgloseOUR && Math.abs(monto) > 0 ? gastosOUR / Math.abs(monto) : 0;
  // OUR > 15% del débito → probablemente la factura no es la correcta.
  const warnOURExcesivo = mostrarDesgloseOUR && ourPorcentaje > 0.15;
  // Fecha de la factura vs fecha del débito desalineadas por más de 1 día.
  const diasDesalineados =
    mostrarDesgloseOUR && facturaSeleccionada?.fecha_emision && movimiento?.fecha
      ? Math.abs(new Date(movimiento.fecha) - new Date(facturaSeleccionada.fecha_emision)) / (1000 * 60 * 60 * 24)
      : 0;
  const warnFechaDesalineada = mostrarDesgloseOUR && diasDesalineados > 1;

  // Propina de restorán (sin DTE): el débito supera la factura SII por la propina, que no genera
  // documento. Misma forma que los Gastos OUR (10.79) pero rama paralela e independiente: el usuario
  // la marca con un toggle, y es mutuamente excluyente con OUR (el toggle se oculta cuando es OUR).
  const puedeMarcarPropina =
    tipoVinculacion === "pago" &&
    modoCompra === "sii" &&
    (movimiento?.tipo === "egreso" || Number(movimiento?.monto || 0) < 0) &&
    !!facturaSeleccionada &&
    Math.abs(monto) > Number(facturaSeleccionada?.monto_total || 0) &&
    !esComisionTransferenciaInternacional;
  const mostrarDesglosePropina =
    esPropina && !!facturaSeleccionada && Math.abs(monto) > Number(facturaSeleccionada?.monto_total || 0);
  const montoPropina = mostrarDesglosePropina ? Math.abs(monto) - Number(facturaSeleccionada.monto_total || 0) : 0;
  // Guardrail blando propina (NO bloquea): propina > 13% de la factura → ¿factura correcta?
  // A diferencia de OUR (% sobre el débito), la base acá es la factura.
  const propinaPorcentaje =
    mostrarDesglosePropina && Number(facturaSeleccionada.monto_total || 0) > 0
      ? montoPropina / Number(facturaSeleccionada.monto_total || 0)
      : 0;
  // Bypass de sobrepago unificado (regla 10.67): OUR y propina son ambos excesos legítimos sin DTE.
  const excesoSinDte = esComisionTransferenciaInternacional || esPropina;

  // La propina se re-evalúa por movimiento: resetear al cambiar de factura o de movimiento.
  useEffect(() => {
    setEsPropina(false);
  }, [facturaSeleccionada, movimiento?.id]);

  // Transferencia internacional RECIBIDA (ingreso USD): Santander netea su comisión de recepción
  // de los USD entrantes antes de acreditar. La factura SII de esa comisión queda huérfana en
  // "Por pagar"; este modal permite vincularla al ingreso vía comisiones_ingreso_neteadas.
  const esTransferenciaRecibida =
    movimiento?.tipo === "ingreso" &&
    movimiento?.moneda === "USD" &&
    /rec\.? ?or\.? ?de pago|liquidaci[oó]n orden de pago|orden de pago/i.test(movimiento?.descripcion_raw || "");

  useEffect(() => {
    if (!show || !pais) return;
    setFacturaSeleccionada(null);
    setFacturasSeleccionadas([]);
    setCuotas([]);
    setCuotaSeleccionada(null);
    setCreditoPrestamo(null);
    setCuotaPrestamo(null);
    setBusqueda("");
    setMostrarDteAnioAnterior(false);
    setEmpleadoId(null);
    setEmpleadoBusqueda("");
    setComponentes([{ ...nuevaLineaComponente(movimiento), monto }]);
    setModoEmpleado("remuneracion");
    setModoCompra("sii");
    setEntidadFallbackId(null);
    setEntidadFallbackBusqueda("");
    setEntidadFallbackOptions([]);
    setSfConcepto("");
    setSfCuentaER("");
    setSfComercio("");
    setSfProveedorId(null);
    setSfProveedorNombre("");
    setAcProveedorId(null);
    setAcProveedorNombre("");
    setAcNota("");
    setAcBucket("");
    setModoCobro("sii");
    setAcCobroProveedorId(null);
    setAcCobroProveedorNombre("");
    setAcCobroNota("");
    setAcCobroBucket("");
    setCreandoProvSII(false);
    setProvSIINombre("");
    setProvSIIRut("");
    setCreandoClienteSII(false);
    setClienteSIIEmpresa("");
    setClienteSIIRut("");
    setPiInstitucion("");
    setPiPeriodoMes(null);
    setPiPeriodoAño(añoActualPI);
    setPiNotas("");
    setEdificiosAsignaciones([]);
    setEdificioSelectorId("");
    setMostrarFacturaEdificio(false);
    setActivoCapexSel(null);
    setActivosCapex([]);
    setFactoringOpIds([]);
    setFactoringTipoVinc("giro");
    setYaVinculadoFactoring(null);
    setVinculacionesSII([]);
    setPagosEmpleadoVinculados([]);
    setExternasSeleccionadas([]);
    setBusquedaExt("");
    setModoExterno("existente");
    setFacturasExtPendientes([]);
    if (movimiento?.id) {
      // MBF + enriquecimiento de las filas SII emitidas con datos de la factura. mbf.monto guarda
      // el monto_total CLP fiscal (correcto para la cobertura), pero el display debe mostrar el
      // monto en su moneda real: para DTE 110 export el valor económico es monto_usd (US$), no el
      // CLP con la etiqueta del movimiento. factura_id es polimórfico → 2ª query por id (no embed).
      (async () => {
        const { data: mbfRows } = await client
          .from("movimiento_bancario_facturas")
          .select("id, factura_tipo, factura_id, monto")
          .eq("movimiento_bancario_id", movimiento.id);
        const filas = mbfRows || [];
        const idsEmitidas = filas.filter((f) => f.factura_tipo === "sii_emitida").map((f) => f.factura_id);
        let facturasById = {};
        if (idsEmitidas.length > 0) {
          const { data: facs } = await client
            .from("facturas_sii_emitidas")
            .select("id, folio, tipo_dte, monto_usd, monto_total")
            .in("id", idsEmitidas);
          facturasById = Object.fromEntries((facs || []).map((f) => [f.id, f]));
        }
        setVinculacionesSII(
          filas.map((f) => (facturasById[f.factura_id] ? { ...f, factura: facturasById[f.factura_id] } : f))
        );
      })();

      // Detectar pagos a empleado vinculados a este movimiento (tercera vía de detección).
      client
        .from("pagos_empleados")
        .select("id, empleado_id, tipo, periodo_año, periodo_mes, monto, moneda, empleado:empleados(nombre, apellido)")
        .eq("movimiento_bancario_id", movimiento.id)
        .then(({ data }) => setPagosEmpleadoVinculados(data || []));
    }
    // Path "Préstamo": créditos activos con cuotas pendientes (solo aplica a egresos).
    if (movimiento?.tipo === "egreso") {
      getCreditosConCuotasPendientes(pais)
        .then(setCreditosDisponibles)
        .catch(() => setCreditosDisponibles([]));
    } else {
      setCreditosDisponibles([]);
    }
    // Cargar facturas externas pendientes para el modo "Vincular existente" del tab Externa.
    // Default inteligente: si hay pendientes → "existente"; si no → "nueva" (form de carga).
    client
      .from("facturas_externas")
      .select(
        "id, proveedor_id, proveedor_nombre, fecha_emision, monto_total, moneda, numero_documento, concepto, cuenta_er, periodo_mes"
      )
      .eq("pais", pais)
      .is("movimiento_bancario_id", null)
      .eq("estado", "pendiente")
      .order("fecha_emision", { ascending: false })
      .then(async ({ data }) => {
        const pendientes = data || [];
        setFacturasExtPendientes(pendientes);
        // Saldo FIFO real por factura (helper canónico, regla 10.1/10.106). Selector país-wide →
        // se llama una vez por proveedor distinto del listado (hoy 2) y se mergea. Fuera del render.
        const provIds = [...new Set(pendientes.map((f) => f.proveedor_id).filter(Boolean))];
        const saldoMap = new Map();
        await Promise.all(
          provIds.map(async (pid) => {
            try {
              const sp = await getSaldosPorFacturaProveedor(pid, pais);
              for (const [key, { saldoDisplay }] of sp) {
                if (key.startsWith("EXT:")) saldoMap.set(Number(key.slice(4)), saldoDisplay);
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("Error computando saldos FIFO por factura externa:", e);
            }
          })
        );
        setSaldosExtMap(saldoMap);
        // "existente" solo si hay facturas con saldo FIFO pendiente; si todas están amortizadas → "nueva".
        const conSaldo = pendientes.filter((f) => saldoMap.has(f.id)).length;
        setModoExterno(conSaldo > 0 ? "existente" : "nueva");
      });
    if (movimiento?.tipo === "ingreso") {
      // Sin filtro de estado: un movimiento puede estar vinculado a una operación LIQUIDADA
      // (no solo activa). Filtrar por "activa" hacía que esos movimientos no se detectaran como
      // ya vinculados y abrieran el form en blanco. Las candidatas para vincular NUEVO igual se
      // acotan abajo por ausencia de FK (giro/remanente), así que incluir liquidadas no agrega ruido.
      getOperacionesFactoring(pais)
        .then((data) => {
          setOperacionesFactoring(data);
          const yaGiro = data.find((op) => op.movimiento_bancario_adelanto_id === movimiento.id);
          const yaExc = data.find((op) => op.movimiento_bancario_remanente_id === movimiento.id);
          if (yaGiro || yaExc) {
            setTipoVinculacion("factoring");
            setFactoringTipoVinc(yaGiro ? "giro" : "excedente");
            setFactoringOpIds([(yaGiro || yaExc).id]);
            setYaVinculadoFactoring(yaGiro || yaExc);
          }
        })
        .catch(() => setOperacionesFactoring([]));
    }
    if (tipoVinculacion === "empleado" || tipoVinculacion === "caja_chica") cargarEmpleados();
    else cargarFacturas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tipoVinculacion, pais, movimiento?.id]);

  // Cargar proveedores al entrar por primera vez en cualquier path con selector de proveedor:
  // pago (externa, sin_factura, a_cuenta) o cobro (a_cuenta).
  useEffect(() => {
    if (!show || !pais) return;
    const necesitaProveedores =
      (tipoVinculacion === "pago" &&
        (modoCompra === "externa" || modoCompra === "sin_factura" || modoCompra === "a_cuenta")) ||
      (tipoVinculacion === "cobro" && modoCobro === "a_cuenta");
    if (!necesitaProveedores) return;
    if (proveedores.length > 0) return;
    (async () => {
      try {
        const data = await getProveedores(pais);
        setProveedores(data);
      } catch {
        toast.error("Error cargando proveedores");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pais, tipoVinculacion, modoCompra, modoCobro]);

  // Path "Préstamo": auto-selección de crédito (si hay uno solo) y de la cuota cuya fecha de
  // vencimiento sea la más cercana a la fecha del movimiento bancario.
  useEffect(() => {
    if (!esPrestamo) return;
    let credito = creditoPrestamo;
    if (!credito && creditosDisponibles.length === 1) {
      credito = creditosDisponibles[0];
      setCreditoPrestamo(credito);
    }
    if (credito && !cuotaPrestamo && credito.cuotas?.length) {
      const ref = new Date(movimiento?.fecha || "2000-01-01").getTime();
      const closest = credito.cuotas.reduce((best, q) => {
        const dist = Math.abs(new Date(q.fecha_vencimiento).getTime() - ref);
        return best == null || dist < best.dist ? { q, dist } : best;
      }, null);
      if (closest) setCuotaPrestamo(closest.q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esPrestamo, creditosDisponibles, creditoPrestamo, cuotaPrestamo, movimiento?.fecha]);

  // Modo "Pago a edificio": listar TODOS los edificios operativos del país con su saldo
  // pendiente al lado como referencia. El saldo viene de getEdificiosSaldoPendiente (mismo
  // helper que FlujoCajaV2; fechaInicio = corte vigente FECHA_CORTE, monedaLocal = moneda del
  // movimiento), PERO ese helper solo devuelve los edificios con saldo > 0. Para no ocultar
  // ninguno (regla del usuario: "elijo el que quiera"), traemos la lista completa de edificios
  // operativos y le hacemos merge del saldo (0 cuando no aparece en el helper).
  useEffect(() => {
    if (!show || !pais || !esPagoEdificio) return;
    if (edificiosSaldo.length > 0) return;
    (async () => {
      try {
        const [saldoData, { data: todosEdificios }] = await Promise.all([
          getEdificiosSaldoPendiente({ pais, fechaInicio: FECHA_CORTE, monedaLocal: moneda }),
          client
            .from("edificios")
            .select("id, edificio, proveedor_id")
            .eq("pais", pais)
            .eq("eliminada", false)
            .in("estado", ESTADOS_COTIZABLE)
            .order("edificio", { ascending: true }),
        ]);
        const saldoPorId = Object.fromEntries((saldoData || []).map((e) => [e.edificioId, e]));
        const merged = (todosEdificios || []).map((ed) => {
          const s = saldoPorId[ed.id];
          return {
            edificioId: Number(ed.id),
            edificioNombre: ed.edificio || `Edificio #${ed.id}`,
            proveedorId: ed.proveedor_id != null ? Number(ed.proveedor_id) : null,
            saldoPendiente: s?.saldoPendiente || 0,
            moneda: s?.moneda || moneda,
          };
        });
        setEdificiosSaldo(merged);
      } catch {
        toast.error("Error cargando edificios");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pais, esPagoEdificio]);

  // Path "Compra de activo" (D.2): listar los activos estado='planificado' del país con su
  // edificio destino, tipo, monto y fecha estimada de pago, para elegir cuál ejecuta este pago.
  useEffect(() => {
    if (!show || !pais || !esPagoActivo) return;
    if (activosCapex.length > 0) return;
    (async () => {
      try {
        const [planificados, { data: edificiosLista }] = await Promise.all([
          getActivos(pais, { estado: "planificado" }),
          client.from("edificios").select("id, edificio").eq("pais", pais),
        ]);
        const nombrePorId = Object.fromEntries((edificiosLista || []).map((e) => [Number(e.id), e.edificio]));
        const conEdificio = (planificados || [])
          .filter((a) => a.destino_tipo === "edificio")
          .map((a) => ({ ...a, edificioNombre: nombrePorId[a.destino_id] || `Edificio #${a.destino_id}` }));
        setActivosCapex(conEdificio);
      } catch {
        toast.error("Error cargando activos planificados");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pais, esPagoActivo]);

  // Path SII: ¿la factura seleccionada ya tiene la entidad cargada por el auto-match de sii-sync?
  // (cliente_id si esCobro, proveedor_id si pago SII). Si no, hace falta el selector inline.
  // Solo aplica en modo single (1 factura) — en múltiple las validaciones del toggle ya
  // exigen entidad cargada en todas.
  const esModoMultiple = facturasSeleccionadas.length >= 2;
  const facturaTieneEntidad =
    facturaSeleccionada && (esCobro ? facturaSeleccionada.cliente_id : facturaSeleccionada.proveedor_id);
  const necesitaSelectorEntidad =
    !esCobro && !esEmpleado && !esPagoExterna && !esModoMultiple && facturaSeleccionada && !facturaTieneEntidad;
  const entidadResueltaId =
    (esCobro ? facturaSeleccionada?.cliente_id : facturaSeleccionada?.proveedor_id) || entidadFallbackId;

  // Detección de vinculación de caja al abrir: usa el embed que ya viene en el prop.
  // Cubre los badges G/A/P/D que no tienen MBF (y donde vinculacionesSII queda vacío).
  // Para V/C/X la caja también está, pero el panel se muestra vía vinculacionesSII (sección A)
  // y la sección B (esta) queda suprimida para evitar duplicar.
  const tieneVinculacionCaja = !!(movimiento?.movimiento_caja_id && movimiento?.movimiento_caja?.categoria_pago);
  const categoriaCajaActual = movimiento?.movimiento_caja?.categoria_pago || null;
  const entidadCajaActual = movimiento?.movimiento_caja?.entidad_nombre || null;
  // Si el movimiento ya tiene alguna vinculación (MBF o caja), el form de revincular
  // queda oculto: la única acción permitida es "Desvincular" desde el panel arriba.
  // Para revincular hay que primero deshacer.
  const yaEstaVinculado = vinculacionesSII.length > 0 || tieneVinculacionCaja || pagosEmpleadoVinculados.length > 0;

  // Modo múltiple: suma de los montos seleccionados y diferencia contra el monto del banco.
  const sumaMontosSeleccionados = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_total || 0), 0);
  const diferenciaMontos = monto - sumaMontosSeleccionados;
  // Exportación (DTE 110): la verdad económica del cobro es USD. Para el DISPLAY de suma/diferencia
  // se usa monto_usd (todas las seleccionables lo tienen) y el monto del movimiento, ya en USD. Esto
  // NO alimenta el cuadre del asiento (sumaCuadraExacto), que queda intacto y exento en exportación.
  const sumaUsdSeleccionadas = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_usd || 0), 0);
  const diferenciaUsd = monto - sumaUsdSeleccionadas;

  // "Costos del giro" (cobro export, modelo 7.18). Franco carga del SWIFT el monto instruido
  // (InstdAmt) y la comisión del banco internacional (Citibank), y del aviso Santander el monto
  // recibido (IntrBkSttlmAmt). Con el recibido como dato INDEPENDIENTE el cuadre es real; sin él
  // cae a "modo parcial" (solo bloquea negativos). `monto` (del movimiento) es el disponible final.
  const giroFacturado = sumaUsdSeleccionadas;
  const giroInstdAmtNum = parseFloat(giroInstdAmt);
  const giroInstdValido = !isNaN(giroInstdAmtNum) && giroInstdAmtNum > 0;
  const giroCitibankNum = parseFloat(giroCitibank) || 0;
  const giroSantanderRecibidoNum = parseFloat(giroSantanderRecibido);
  const giroRecibidoValido = !isNaN(giroSantanderRecibidoNum) && giroSantanderRecibidoNum > 0;
  // Place = facturado − instruido (siempre por diferencia: no hay dato independiente del take).
  const giroComisionPlace = giroInstdValido ? giroFacturado - giroInstdAmtNum : 0;
  // Santander: con recibido cargado sale del dato INDEPENDIENTE (recibido − disponible); sin él
  // cae al despeje (instruido − Citibank − disponible) = el viejo modo tautológico.
  const giroComisionSantander = giroRecibidoValido
    ? giroSantanderRecibidoNum - monto
    : giroInstdValido
      ? giroInstdAmtNum - giroCitibankNum - monto
      : 0;
  // Cuadre REAL (solo con recibido): dos validaciones que ahora SÍ pueden fallar.
  //  (a) escalón intermedio: instruido − Citibank == recibido Santander (SWIFT vs aviso).
  //  (b) cascada completa: facturado − Place − Citibank − Santander == disponible.
  // Numéricamente (a)≡(b) cuando Santander sale del recibido, pero ya NO es tautológico: ambas
  // fallan juntas si el SWIFT y el aviso no concuerdan (recibido es un dato independiente).
  const giroCuadreA = giroInstdAmtNum - giroCitibankNum - giroSantanderRecibidoNum;
  const giroCuadreB = giroFacturado - giroComisionPlace - giroCitibankNum - giroComisionSantander - monto;
  const giroCuadreAOk = Math.abs(giroCuadreA) < 0.5;
  const giroCuadreBOk = Math.abs(giroCuadreB) < 0.5;
  const giroCuadreReal = giroInstdValido && giroRecibidoValido && giroCuadreAOk && giroCuadreBOk;
  const giroModoParcial = giroInstdValido && !giroRecibidoValido;
  // Gate del botón: instruido + ≥1 factura + componentes no negativos. Con recibido exige además
  // el cuadre real; sin recibido (modo parcial) solo bloquea negativos.
  const costosGiroValidos =
    giroInstdValido &&
    facturasSeleccionadas.length > 0 &&
    giroComisionPlace >= -0.5 &&
    giroComisionSantander >= -0.5 &&
    (giroModoParcial || giroCuadreReal);

  const sumaCuadraExacto = esCobroExportacion
    ? Math.abs(diferenciaMontos) / monto < 0.15
    : Math.abs(diferenciaMontos) < 0.5;

  // Wizard export: arrancar siempre en el Paso 1 al abrir el modal o cambiar de movimiento/modo.
  useEffect(() => {
    setPasoExport(1);
  }, [movimiento?.id, modoCobro]);

  // "Cargar SWIFT (PDF)": parsea el PDF en memoria (sin subirlo) y autocompleta los inputs del
  // Paso 2. El cuadre (✓/✗) valida solo, como si Franco hubiera tipeado. Si falla el parseo o el
  // PDF no es un SWIFT, mensaje claro y los inputs quedan como estaban (carga manual disponible).
  const handleCargarSwift = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-subir el mismo archivo
    if (!file) return;
    setCargandoSwift(true);
    try {
      const r = await parsearSwiftDesdePdf(file);
      if (r.instdAmt != null) setGiroInstdAmt(String(r.instdAmt));
      if (r.citibank != null) setGiroCitibank(String(r.citibank));
      if (r.recibidoSantander != null) setGiroSantanderRecibido(String(r.recibidoSantander));
      if (r.uetr) setGiroUetr(r.uetr);
      if (r.ordenante) setGiroOrdenante(r.ordenante);
      if (r.bic) setGiroOrdenanteBic(r.bic);
      if (r.fechaValor) setGiroFechaValor(r.fechaValor);
      if (r.uetr || r.ordenante || r.bic || r.fechaValor) setGiroSwiftOpen(true);
      toast.success("SWIFT cargado — verificá el cuadre");
    } catch {
      toast.error("No se pudo leer el SWIFT — verificá que sea el PDF correcto");
    } finally {
      setCargandoSwift(false);
    }
  };

  // Cargar opciones de entidad fallback (clientes para cobro, proveedores para pago SII)
  useEffect(() => {
    if (!necesitaSelectorEntidad) return;
    if (entidadFallbackOptions.length > 0) return;
    (async () => {
      try {
        const data = esCobro ? await getClientesActivos(pais) : await getProveedores(pais);
        setEntidadFallbackOptions(data);
      } catch {
        toast.error("Error cargando opciones de entidad");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [necesitaSelectorEntidad, esCobro, pais]);

  const cargarFacturas = async () => {
    setLoading(true);
    try {
      const [data, vinculadas] = await Promise.all([
        esCobro ? getFacturasEmitidas(pais) : getFacturasRecibidas(pais),
        getFacturasYaVinculadas(pais),
      ]);
      const yaVinculadas = esCobro ? vinculadas.emitidas : vinculadas.recibidas;
      const montosAcum = esCobro ? vinculadas.montosAcumEmitidas : vinculadas.montosAcumRecibidas;
      const lista = data
        .filter((f) => !yaVinculadas.has(f.id))
        .map((f) => {
          // Factura parcialmente pagada por vínculos previos (N movimientos → 1 factura):
          // mostrar el saldo restante en vez del total, conservando el original aparte.
          const yaVinculado = montosAcum?.get(f.id) || 0;
          return yaVinculado > 0
            ? {
                ...f,
                monto_total_original: f.monto_total,
                monto_total: Number(f.monto_total) - yaVinculado,
                _parcial: true,
              }
            : f;
        })
        .filter((f) => Number(f.monto_total) > 0.01);

      // Enriquecer facturas de exportación (DTE 110) con el nombre del cliente de la venta
      // vinculada (factura → venta_ref_id → sales.cliente). UN solo fetch para todos los
      // venta_ref_id, nunca una query por fila. Solo para display en el sub-tab Exportación.
      const refIds = [
        ...new Set(
          lista
            .filter((f) => f.tipo_dte === 110 && f.venta_tipo === "sales" && f.venta_ref_id != null)
            .map((f) => f.venta_ref_id)
        ),
      ];
      let clientePorRef = {};
      if (refIds.length > 0) {
        const { data: ventas } = await client.from("sales").select("id, cliente").in("id", refIds);
        clientePorRef = (ventas || []).reduce((acc, v) => ({ ...acc, [v.id]: v.cliente }), {});
      }

      setFacturas(
        lista.map((f) =>
          f.tipo_dte === 110 && f.venta_ref_id != null && clientePorRef[f.venta_ref_id]
            ? { ...f, _clienteNombre: clientePorRef[f.venta_ref_id] }
            : f
        )
      );
    } catch {
      toast.error("Error cargando facturas");
    } finally {
      setLoading(false);
    }
  };

  const cargarEmpleados = async () => {
    setLoading(true);
    try {
      const [data, finiquitados] = await Promise.all([getEmpleados(pais), getEmpleadoIdsConFiniquito(pais)]);
      const fechaMov = parseFechaLocal(movimiento?.fecha);
      // Activos (sin fin_contrato o aún vigente a la fecha del movimiento) siempre se muestran.
      // Egresados se muestran solo si son recientes (egreso_fecha >= FECHA_CORTE) y todavía no
      // tienen un pago de finiquito registrado — para poder vincularles ese finiquito.
      const visibles = data
        .map((e) => ({
          ...e,
          _esEgresado: !!e.fin_contrato && parseFechaLocal(e.fin_contrato) < fechaMov,
        }))
        .filter((e) => {
          if (!e._esEgresado) return true;
          return e.egreso_fecha && e.egreso_fecha >= FECHA_CORTE && !finiquitados.has(e.id);
        });
      setEmpleados(visibles);
      const desc = (movimiento?.descripcion_raw || "").toLowerCase();
      const match = visibles.find((e) => {
        const nombre = (e.nombre || "").toLowerCase();
        const apellido = (e.apellido || "").toLowerCase();
        return nombre && apellido && desc.includes(nombre) && desc.includes(apellido);
      });
      if (match) setEmpleadoId(match.id);
    } catch {
      toast.error("Error cargando empleados");
    } finally {
      setLoading(false);
    }
  };

  // Toggle de selección. Click en factura ya seleccionada la quita; click en otra la agrega.
  // Modo múltiple (length>=2): valida que todas sean del mismo proveedor/cliente y tengan
  // entidad cargada (no NULL). En modo single se preserva el flujo actual con cuotas.
  const handleSeleccionarFactura = async (f) => {
    const yaEsta = facturasSeleccionadas.some((x) => x.id === f.id);

    // Exportación: solo las facturas con snapshot USD (monto_usd) son cobrables en este flujo USD.
    // Las sin USD se muestran deshabilitadas; este guard es defensa en profundidad (bloquea AGREGAR,
    // no QUITAR). Ver display en la lista del sub-tab Exportación.
    if (!yaEsta && esCobroExportacion && f.monto_usd == null) return;

    // QUITAR
    if (yaEsta) {
      const restantes = facturasSeleccionadas.filter((x) => x.id !== f.id);
      setFacturasSeleccionadas(restantes);
      setCuotaSeleccionada(null);
      if (restantes.length === 0) {
        setFacturaSeleccionada(null);
        setCuotas([]);
      } else if (restantes.length === 1) {
        // Volvemos a single con la que quedó: cargar cuotas si tiene
        const unica = restantes[0];
        setFacturaSeleccionada(unica);
        try {
          const plan = esCobro ? await getPlanCobros(unica.id) : await getPlanPagos(unica.id);
          setCuotas(plan.filter((c) => c.estado === "pendiente"));
        } catch {
          setCuotas([]);
        }
      } else {
        setFacturaSeleccionada(restantes.at(-1));
        setCuotas([]);
      }
      return;
    }

    // AGREGAR — validaciones para modo múltiple
    if (facturasSeleccionadas.length > 0) {
      // Exportación (DTE 110): cliente_id es el anunciante de fondo y varía por campaña, pero el
      // cobro USD lo liquida un único receptor fiscal (intermediario). Validar igualdad de receptor
      // en vez de cliente. Se usa razon_social_receptor porque rut_receptor es el genérico
      // 55555555-5 para todo receptor extranjero y no discrimina entre intermediarios distintos.
      if (esCobroExportacion) {
        const receptorEsperado = facturasSeleccionadas[0].razon_social_receptor;
        if (receptorEsperado && f.razon_social_receptor !== receptorEsperado) {
          return toast.error("Todas las facturas deben ser del mismo receptor (intermediario).");
        }
      } else {
        const entidadCampo = esCobro ? "cliente_id" : "proveedor_id";
        const entidadEsperada = facturasSeleccionadas[0][entidadCampo];
        if (!f[entidadCampo] && !esCobro) {
          return toast.error(
            "La factura no tiene proveedor asociado. En modo múltiple, vinculá esta factura primero en single para asignarle entidad."
          );
        }
        if (entidadEsperada && f[entidadCampo] !== entidadEsperada) {
          return toast.error(`Todas las facturas deben ser del mismo ${esCobro ? "cliente" : "proveedor"}.`);
        }
      }
    }

    const nueva = [...facturasSeleccionadas, f];
    setFacturasSeleccionadas(nueva);
    setFacturaSeleccionada(f);
    setCuotaSeleccionada(null);
    setEntidadFallbackId(null);
    setEntidadFallbackBusqueda("");
    setCreandoProvSII(false);
    setProvSIINombre("");
    setProvSIIRut("");
    setCreandoClienteSII(false);
    setClienteSIIEmpresa("");
    setClienteSIIRut("");

    if (nueva.length === 1) {
      try {
        const plan = esCobro ? await getPlanCobros(f.id) : await getPlanPagos(f.id);
        setCuotas(plan.filter((c) => c.estado === "pendiente"));
      } catch {
        setCuotas([]);
      }
    } else {
      // Modo múltiple: sin cuotas
      setCuotas([]);
    }
  };

  // Toggle de selección para facturas externas pendientes. Misma regla que SII:
  // todas las facturas seleccionadas deben ser del mismo proveedor (comparación por ID).
  const handleSeleccionarExterna = (f) => {
    const yaEsta = externasSeleccionadas.some((x) => x.id === f.id);
    if (yaEsta) {
      setExternasSeleccionadas(externasSeleccionadas.filter((x) => x.id !== f.id));
      return;
    }
    if (externasSeleccionadas.length > 0 && f.proveedor_id !== externasSeleccionadas[0].proveedor_id) {
      return toast.error("Todas las facturas deben ser del mismo proveedor.");
    }
    setExternasSeleccionadas([...externasSeleccionadas, f]);
  };

  // Crea un proveedor desde el selector inline del path Pago SII y lo deja seleccionado.
  // El nombre y el RUT se pre-cargan desde la factura SII para minimizar fricción.
  const handleCrearProveedorDesdeSII = async () => {
    if (!provSIINombre.trim()) return toast.error("Ingresá el nombre del proveedor");
    try {
      const nuevo = await crearProveedor({
        pais,
        nombre: provSIINombre.trim(),
        rut_ruc: provSIIRut.trim() || null,
        habilitado: true,
      });
      // Ya existe uno con ese RUT (guard anti-duplicado): usar el existente en vez de crear.
      const entidad = nuevo?.yaExiste ? nuevo.proveedor : nuevo;
      if (!entidad?.id) throw new Error("No se pudo crear el proveedor");
      setEntidadFallbackOptions((prev) =>
        prev.some((p) => p.id === entidad.id)
          ? prev
          : [...prev, entidad].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      );
      setEntidadFallbackId(entidad.id);
      setCreandoProvSII(false);
      setProvSIINombre("");
      setProvSIIRut("");
      if (nuevo?.yaExiste) {
        toast(`Ya existe un proveedor con RUT ${entidad.rut_ruc || ""}: ${entidad.nombre}. Se usó el existente.`);
      } else {
        toast.success("Proveedor creado y seleccionado");
      }
    } catch (err) {
      toast.error("Error creando proveedor: " + (err.message || ""));
    }
  };

  // Simétrico al anterior pero para clientes (path Cobro SII).
  const handleCrearClienteDesdeSII = async () => {
    if (!clienteSIIEmpresa.trim()) return toast.error("Ingresá la razón social del cliente");
    try {
      const nuevo = await crearClienteMinimal({
        pais,
        empresa: clienteSIIEmpresa.trim(),
        nombre: clienteSIIEmpresa.trim(),
        rut: clienteSIIRut.trim() || null,
      });
      // Ya existe uno con ese RUT (guard anti-duplicado): usar el existente en vez de crear.
      const entidad = nuevo?.yaExiste ? nuevo.cliente : nuevo;
      if (!entidad?.id) throw new Error("No se pudo crear el cliente");
      setEntidadFallbackOptions((prev) =>
        prev.some((c) => c.id === entidad.id)
          ? prev
          : [...prev, entidad].sort((a, b) => (a.empresa || a.nombre || "").localeCompare(b.empresa || b.nombre || ""))
      );
      setEntidadFallbackId(entidad.id);
      setCreandoClienteSII(false);
      setClienteSIIEmpresa("");
      setClienteSIIRut("");
      if (nuevo?.yaExiste) {
        toast(
          `Ya existe un cliente con RUT ${entidad.rut || ""}: ${entidad.empresa || entidad.nombre}. Se usó el existente.`
        );
      } else {
        toast.success("Cliente creado y seleccionado");
      }
    } catch (err) {
      toast.error("Error creando cliente: " + (err.message || ""));
    }
  };

  const actualizarComponente = (idx, campo, valor) => {
    setComponentes((prev) => prev.map((c, i) => (i === idx ? { ...c, [campo]: valor } : c)));
  };
  const agregarComponente = () => {
    const restante = monto - totalComponentes;
    setComponentes((prev) => [
      ...prev,
      { ...nuevaLineaComponente(movimiento, "bono"), monto: restante > 0 ? restante : "" },
    ]);
  };
  const eliminarComponente = (idx) => {
    setComponentes((prev) => prev.filter((_, i) => i !== idx));
  };

  const totalComponentes = useMemo(() => componentes.reduce((s, c) => s + Number(c.monto || 0), 0), [componentes]);
  const diferencia = monto - totalComponentes;
  const cuadra = Math.abs(diferencia) < 0.5;

  // --- Modo "Pago a edificio": helpers de asignación multi-edificio + cuadre exacto ---
  const agregarEdificioAsignacion = () => {
    const id = Number(edificioSelectorId);
    if (!id) return;
    if (edificiosAsignaciones.some((a) => a.edificioId === id)) return; // ya agregado
    const ed = edificiosSaldo.find((e) => e.edificioId === id);
    if (!ed) return;
    // Primer edificio (reparto vacío): autocompletar con el monto total del movimiento → queda
    // cuadrado y el confirmar se habilita solo (caso 1 edificio, el más común). Si ya hay filas,
    // el nuevo entra en 0 y el usuario reparte a mano (NO redistribuimos los montos ya tipeados).
    const esPrimero = edificiosAsignaciones.length === 0;
    setEdificiosAsignaciones((prev) => [
      ...prev,
      {
        edificioId: id,
        edificioNombre: ed.edificioNombre,
        proveedorId: ed.proveedorId ?? null,
        monto: esPrimero ? Math.abs(monto) : "",
      },
    ]);
    setEdificioSelectorId("");
  };
  const actualizarEdificioAsignacion = (edificioId, valor) => {
    setEdificiosAsignaciones((prev) => prev.map((a) => (a.edificioId === edificioId ? { ...a, monto: valor } : a)));
  };
  const quitarEdificioAsignacion = (edificioId) => {
    setEdificiosAsignaciones((prev) => prev.filter((a) => a.edificioId !== edificioId));
  };

  // Cuadre EXACTO (tolerancia 0): Σ montos tipeados === abs(monto del movimiento). Moneda local,
  // mismo signo, sin conversión → no hay drift cambiario que tolerar.
  const totalEdificiosAsignado = edificiosAsignaciones.reduce((s, a) => s + Number(a.monto || 0), 0);
  const montoAbsMovimiento = Math.abs(monto);
  const restanteEdificios = montoAbsMovimiento - totalEdificiosAsignado;
  const edificiosCuadra = edificiosAsignaciones.length > 0 && restanteEdificios === 0;
  // Guard de validación del "Pago a edificio" (se usa en confirmarDisabled). Requiere solo:
  // al menos un edificio + reparto que cuadra exacto + todos los montos > 0. La factura es
  // adjunto opcional de soporte: NO es requisito y no bloquea el confirmar.
  const edificioConfirmarDisabled =
    edificiosAsignaciones.length === 0 || !edificiosCuadra || edificiosAsignaciones.some((a) => Number(a.monto) <= 0);

  const handleConfirmarEmpleado = async () => {
    if (!empleadoId) return toast.error("Seleccioná un empleado");
    if (componentes.length === 0) return toast.error("Agregá al menos un componente");
    if (!cuadra) {
      return toast.error(
        `La suma (${formatAmount(totalComponentes, moneda)}) debe igualar el monto del movimiento (${formatAmount(
          monto,
          moneda
        )})`
      );
    }
    for (const c of componentes) {
      if (!c.tipo) return toast.error("Faltó tipo en un componente");
      if (TIPOS_REQUIEREN_PERIODO.includes(c.tipo) && (!c.periodo_año || !c.periodo_mes)) {
        return toast.error(`Tipo "${c.tipo}" requiere período (año/mes)`);
      }
      if (!c.monto || Number(c.monto) <= 0) return toast.error("Monto debe ser > 0 en todos los componentes");
    }
    setLoading(true);
    try {
      const rows = componentes.map((c) => ({
        empleado_id: empleadoId,
        tipo: c.tipo,
        periodo_año: c.periodo_año ? Number(c.periodo_año) : null,
        periodo_mes: c.periodo_mes ? Number(c.periodo_mes) : null,
        monto: Number(c.monto),
        moneda,
        fecha_pago: movimiento.fecha,
        movimiento_bancario_id: movimiento.id,
        notas: c.notas?.trim() ? c.notas.trim() : null,
        created_by: usuarioId || null,
      }));
      const pagosInsertados = await crearPagosEmpleado(rows);
      toast.success("Pago a empleado registrado");
      // Empleado: el badge E depende de pagosEmpleadosRegistrados (state separado en
      // el padre). Pasamos las filas insertadas para que el padre las inyecte a ese
      // state (update optimista) en lugar de recargar todo.
      onSuccess?.({ id: movimiento.id, _empleado: true, _pagosEmpleado: pagosInsertados });
      onClose();
    } catch (err) {
      toast.error("Error al registrar pago: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Caja chica: categoriza un egreso bancario como entrega de fondos a un empleado.
  // Escribe en movimientos_caja (categoria_pago='caja_chica', entidad_tipo='empleado') y
  // confirma el bancario — NO usa pagos_empleados. entidad_id es INTEGER y referencia
  // usuarios.id, así que necesitamos el usuario_id del empleado (no su id UUID). Si el
  // empleado no tiene usuario vinculado, abortamos con mensaje claro (sin esto el insert
  // tiraría "invalid input syntax for type integer").
  const handleConfirmarCajaChica = async () => {
    if (!empleadoId) return toast.error("Seleccioná un empleado");
    const emp = empleados.find((e) => String(e.id) === String(empleadoId));
    if (!emp) return toast.error("Empleado no encontrado");
    if (!emp.usuario_id) {
      return toast.error(`${emp.nombre} no tiene usuario vinculado — no se puede registrar caja chica`);
    }
    const usuarioIdEmp = emp.usuario_id;

    setLoading(true);
    try {
      const movCaja = {
        tipo: "pago",
        fecha: movimiento.fecha,
        monto: Number(movimiento.monto),
        moneda: movimiento.moneda || moneda,
        estado: "confirmado",
        cuenta_banco: movimiento.cuenta_bancaria_id,
        entidad_tipo: "empleado",
        entidad_id: usuarioIdEmp,
        entidad_nombre: `${emp.nombre} ${emp.apellido}`.trim(),
        categoria_pago: "caja_chica",
        descripcion: movimiento.descripcion_raw || "Caja chica",
        pais,
        registrado_por: usuarioId,
        movimiento_bancario_id: movimiento.id,
        origen: "clay",
      };

      const { data: cajaData, error: cajaError } = await client
        .from("movimientos_caja")
        .upsert(movCaja, { onConflict: "movimiento_bancario_id" })
        .select()
        .single();
      if (cajaError) throw cajaError;

      await client
        .from("movimientos_bancarios")
        .update({
          estado: "procesado",
          movimiento_caja_id: cajaData.id,
          procesado_por: usuarioId,
          procesado_at: new Date().toISOString(),
        })
        .eq("id", movimiento.id);

      toast.success(`Vinculado a caja chica de ${emp.nombre}`);
      onSuccess?.({
        id: movimiento.id,
        movimiento_caja_id: cajaData.id,
        movimiento_caja: cajaData,
        estado: "procesado",
      });
      onClose();
    } catch (err) {
      toast.error("Error: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarSinFactura = async () => {
    if (!sfProveedorId) return toast.error("Seleccioná un proveedor");
    // P3 (red de seguridad): un proveedor que emite factura no puede cerrarse como gasto sin factura,
    // aunque el usuario llegue al confirm por un camino que esquive el gate del tab. Por id (10.7).
    if (proveedores.find((p) => p.id === Number(sfProveedorId))?.es_extranjero) {
      return toast.error(`${sfProveedorNombre || "Este proveedor"} emite factura — cargala en "Factura externa"`);
    }
    if (!sfConcepto.trim()) return toast.error("Ingresá el concepto del gasto");
    if (!sfCuentaER) return toast.error("Seleccioná la cuenta del Estado de Resultados");
    setLoading(true);
    try {
      // Idempotencia por movimiento_bancario_id
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: movimiento.descripcion_raw || movimiento.descripcion || sfConcepto.trim(),
          entidad_tipo: "proveedor",
          entidad_id: sfProveedorId,
          entidad_nombre: sfProveedorNombre,
          categoria_pago: "gasto_sin_factura",
          cuenta_er: sfCuentaER,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success("Gasto sin factura registrado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: "gasto_sin_factura" },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarACuenta = async () => {
    if (!acProveedorId) return toast.error("Seleccioná un proveedor");
    const categoriaResuelta = acBucket === "factura" ? "pago_factura" : "pago_credito";
    setLoading(true);
    try {
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: acNota.trim() || movimiento.descripcion_raw || movimiento.descripcion || "",
          entidad_tipo: "proveedor",
          entidad_id: acProveedorId,
          entidad_nombre: acProveedorNombre,
          categoria_pago: categoriaResuelta,
          cuenta_er: null,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success("Pago a cuenta registrado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: categoriaResuelta },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo múltiple: 1 mov bancario vincula a N facturas SII (mismo proveedor/cliente).
  // Las FKs directas en movimientos_bancarios.factura_sii_*_id quedan en NULL; las N
  // vinculaciones se registran en la tabla movimiento_bancario_facturas. Se crea 1 sola
  // fila en movimientos_caja con el monto del banco y monto_neto/monto_impuesto sumados.
  const handleConfirmarMultiple = async () => {
    if (!sumaCuadraExacto && !esCobroExportacion) {
      return toast.error("La suma de las facturas debe coincidir exacto con el monto del movimiento.");
    }
    const entidadCampo = esCobro ? "cliente_id" : "proveedor_id";
    const entidadId = facturasSeleccionadas[0][entidadCampo];
    const entidadNombre = esCobro
      ? facturasSeleccionadas[0].razon_social_receptor
      : facturasSeleccionadas[0].razon_social_emisor;
    setLoading(true);
    try {
      // 1. UPDATE movimientos_bancarios (FKs directas en NULL — son múltiples)
      const updateMov = {
        factura_sii_emitida_id: null,
        factura_sii_recibida_id: null,
        estado: "procesado",
        procesado_por: usuarioId || null,
        procesado_at: new Date().toISOString(),
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        match_factura_tipo: null,
        match_factura_id: null,
        match_confianza: null,
        match_razon: null,
      };
      await client.from("movimientos_bancarios").update(updateMov).eq("id", movimiento.id);

      // 2. INSERT N filas en movimiento_bancario_facturas
      const filasMBF = facturasSeleccionadas.map((f) => ({
        movimiento_bancario_id: movimiento.id,
        factura_tipo: esCobro ? "sii_emitida" : "sii_recibida",
        factura_id: f.id,
        monto: f.monto_total,
      }));
      const { error: errMBF } = await client.from("movimiento_bancario_facturas").insert(filasMBF);
      if (errMBF) throw errMBF;

      // 3. UPDATE cada factura SII: vinculada + plazopago/facturafechacobro
      for (const f of facturasSeleccionadas) {
        const tabla = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
        const update = {
          estado_vinculacion: "vinculada",
          vinculado_por: usuarioId || null,
          vinculado_at: new Date().toISOString(),
        };
        if (esCobro) update.facturafechacobro = movimiento.fecha;
        else update.plazopago = movimiento.fecha;
        const { error: errF } = await client.from(tabla).update(update).eq("id", f.id);
        if (errF) throw errF;

        // Replicar a tabla 1:N facturas_sii_vinculaciones (solo emitidas con venta legacy
        // ya populada). Sin esto el modal de DTE las muestra como pendientes.
        if (esCobro && f.venta_tipo && f.venta_ref_id != null) {
          await client.from("facturas_sii_vinculaciones").upsert(
            {
              factura_sii_emitida_id: f.id,
              venta_tipo: f.venta_tipo,
              venta_ref_id: f.venta_ref_id,
              venta_ref_index: f.venta_ref_index ?? null,
              vinculado_por: usuarioId || null,
              vinculado_at: new Date().toISOString(),
            },
            { onConflict: "factura_sii_emitida_id,venta_tipo,venta_ref_id" }
          );
        }
      }

      // 4. INSERT movimientos_caja (idempotente por movimiento_bancario_id)
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaIdMultiple = existente?.id || null;
      if (!existente) {
        const totalNeto = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_neto || 0), 0);
        const totalIva = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_iva || 0), 0);
        const folios = facturasSeleccionadas.map((f) => f.folio).join(", ");
        const movCaja = await crearMovimiento({
          pais,
          tipo: esCobro ? "cobro" : "pago",
          fecha: movimiento.fecha,
          monto, // del movimiento bancario
          monto_neto: totalNeto,
          monto_impuesto: totalIva,
          moneda: movimiento.moneda || (pais === "Chile" ? "CLP" : "PEN"),
          estado: "confirmado",
          descripcion: `${entidadNombre} — Folios ${folios}`,
          entidad_tipo: esCobro ? "cliente" : "proveedor",
          entidad_id: entidadId,
          entidad_nombre: entidadNombre,
          categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor",
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (movCaja?.id) {
          await client.from("movimientos_bancarios").update({ movimiento_caja_id: movCaja.id }).eq("id", movimiento.id);
          movCajaIdMultiple = movCaja.id;
        }
      } else if (!esCobro) {
        // Formalización de un pago a cuenta preexistente contra facturas SII recibidas (múltiple): la
        // fila de caja ya existía (bucket 'pago_factura'/'pago_credito' → badge A). Reclasificar a
        // 'pago_proveedor' para que el badge pase de A a C. Solo recibidas (!esCobro); NO toca el
        // motor de imputación del ledger (los MBF ya saldan las facturas). Ver 4.2 / 4.9.2.
        await client.from("movimientos_caja").update({ categoria_pago: "pago_proveedor" }).eq("id", existente.id);
      }

      // 5. Cobro export (modelo 7.18): registrar el giro internacional (transferencias_internacionales)
      //    y derivar los costos de la capa programática (costos_intermediacion), prorrateados por
      //    factura. Idempotente por movimiento_bancario_id. monto_real poblado (es el real del SWIFT);
      //    la capa agencia_local NO se escribe acá (es fase 6). Si la transferencia falla, no se
      //    escriben costos (throw → lo captura el catch).
      if (esCobroExportacion) {
        const { data: giroExistente } = await client
          .from("transferencias_internacionales")
          .select("id")
          .eq("movimiento_bancario_id", movimiento.id)
          .maybeSingle();
        if (!giroExistente) {
          // Resolver entidades por lookup (NO hardcodear ids: pueden cambiar). Place en
          // intermediarios; Citibank y Santander en proveedores (Santander por RUT 97036000-K
          // para no confundir con "Santander - Comisiones" id 57). Si falta alguna → id null +
          // nombre (no rompe el confirm) + warning.
          const [placeRes, citiRes, santaRes] = await Promise.all([
            client.from("intermediarios").select("id").ilike("nombre", "%place exchange%").limit(1).maybeSingle(),
            client.from("proveedores").select("id").ilike("nombre", "%citi%").limit(1).maybeSingle(),
            client.from("proveedores").select("id").eq("rut_ruc", "97036000-K").limit(1).maybeSingle(),
          ]);
          const placeId = placeRes.data?.id ?? null;
          const citibankId = citiRes.data?.id ?? null;
          const santanderId = santaRes.data?.id ?? null;
          const faltantes = [];
          if (placeId == null) faltantes.push("Place Exchange (intermediarios)");
          if (citibankId == null) faltantes.push("Citibank (proveedores)");
          if (santanderId == null) faltantes.push("Santander 97036000-K (proveedores)");
          if (faltantes.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(
              `[costos_intermediacion] entidad no resuelta, se escribe entidad_id=null: ${faltantes.join(", ")}`
            );
          }

          const referenciaGiro = (movimiento.descripcion_raw || "").match(/Rec Or de Pago\s+(\d+)/)?.[1] || null;
          const { data: giroRow, error: errGiro } = await client
            .from("transferencias_internacionales")
            .insert({
              referencia: referenciaGiro,
              movimiento_bancario_id: movimiento.id,
              ordenante_nombre: giroOrdenante.trim() || null,
              ordenante_banco_bic: giroOrdenanteBic.trim() || null,
              monto_instruido: giroInstdAmtNum,
              comision_banco_internacional: giroCitibankNum,
              // Recibido por el banco local: el dato independiente del aviso si se cargó; si no,
              // el despeje instruido − Citibank (modo parcial).
              monto_recibido_banco_local: giroRecibidoValido
                ? giroSantanderRecibidoNum
                : giroInstdAmtNum - giroCitibankNum,
              moneda: "USD",
              fecha_valor: giroFechaValor || null,
              uetr: giroUetr.trim() || null,
              fuente: "manual",
              pais,
              registrado_por: usuarioId || null,
            })
            .select("id")
            .single();
          if (errGiro) throw errGiro;
          const giroId = giroRow.id;

          // Prorrateo por factura (peso = monto_usd / facturado). El residuo de redondeo va a la
          // última factura para que Σ partes == total del giro exacto.
          const prorratear = (total) => {
            const partes = facturasSeleccionadas.map(
              (f) => Math.round(total * (Number(f.monto_usd || 0) / giroFacturado) * 100) / 100
            );
            const sumaParcial = partes.reduce((s, p) => s + p, 0);
            const lastIdx = partes.length - 1;
            if (lastIdx >= 0) {
              partes[lastIdx] = Math.round((partes[lastIdx] + (total - sumaParcial)) * 100) / 100;
            }
            return partes;
          };
          const partesPlace = prorratear(giroComisionPlace);
          const partesCitibank = prorratear(giroCitibankNum);
          const partesSantander = prorratear(giroComisionSantander);
          const periodoDevengo = `${movimiento.fecha.substring(0, 7)}-01`;

          const filasCostos = [];
          facturasSeleccionadas.forEach((f, i) => {
            const comun = {
              venta_tipo: f.venta_tipo,
              venta_ref_id: f.venta_ref_id,
              base_calculo: Number(f.monto_usd || 0),
              monto_teorico: null, // real directo del SWIFT, no devengo teórico
              moneda: "USD",
              fuente: "manual",
              giro_id: giroId,
              periodo_devengo: periodoDevengo,
              pais,
              registrado_por: usuarioId || null,
              reduce_base_comision: true,
            };
            filasCostos.push(
              {
                ...comun,
                tipo_costo: "intermediario_programatico",
                entidad_tipo: "intermediario",
                entidad_id: placeId, // Place Exchange Inc (lookup intermediarios)
                entidad_nombre: "Place Exchange Inc",
                monto_real: partesPlace[i],
              },
              {
                ...comun,
                tipo_costo: "banco_internacional",
                entidad_tipo: "proveedor",
                entidad_id: citibankId, // Citibank (lookup proveedores)
                entidad_nombre: "Citibank",
                monto_real: partesCitibank[i],
              },
              {
                ...comun,
                tipo_costo: "banco_local",
                entidad_tipo: "proveedor",
                entidad_id: santanderId, // Santander 97036000-K (lookup proveedores)
                entidad_nombre: "Santander",
                monto_real: partesSantander[i],
              }
            );
          });
          const { error: errCostos } = await client.from("costos_intermediacion").insert(filasCostos);
          if (errCostos) throw errCostos;
        }
      }

      toast.success(`${facturasSeleccionadas.length} facturas vinculadas`);
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        factura_sii_emitida_id: null,
        factura_sii_recibida_id: null,
        movimiento_caja_id: movCajaIdMultiple,
        movimiento_caja: { categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor" },
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo "Factura externa → Vincular existente": vincula 1..N facturas externas pendientes
  // del mismo proveedor a este movimiento bancario. Persistencia:
  //   1) movimiento_bancario_facturas: N filas con factura_tipo='factura_externa'.
  //   2) facturas_externas: estado='pagada' + movimiento_bancario_id=mov.id por cada una.
  //   3) movimientos_bancarios: match_* + estado='procesado'. En single conserva
  //      match_factura_id; en multi va NULL (no hay FK escalar para N).
  //   4) movimientos_caja: UN solo row con monto del banco, descripcion concatena docs.
  const handleConfirmarVincularExternas = async () => {
    if (externasSeleccionadas.length === 0) return toast.error("Seleccioná al menos una factura");
    // Guard: no se vincula una factura con saldo PARCIAL. El confirm usa monto_total (marca la factura
    // 100% pagada + MBF por el total + cuadre contra el total) → vincular una parcial haría doble
    // conteo del pago a cuenta ya amortizado por FIFO. El residuo es diferencia de cambio, no deuda:
    // se sincera desde Estado de Cuenta (ajuste futuro), no por banco.
    const parcial = externasSeleccionadas.find((f) => f._parcial);
    if (parcial) {
      return toast.error(
        `No se puede vincular "${parcial.numero_documento || parcial.concepto || `Doc ${parcial.id}`}" con saldo parcial (${formatAmount(
          parcial.saldoDisplay,
          parcial.moneda || moneda
        )}) — el residuo es diferencia de cambio, no deuda por pagar. Se sincera desde Estado de Cuenta.`
      );
    }
    if (!extCuadra) {
      return toast.error(
        "La suma de las facturas debe coincidir con el monto del movimiento (tolerancia 1%, 5% con conversión de moneda)"
      );
    }
    setLoading(true);
    try {
      const provId = externasSeleccionadas[0].proveedor_id;
      const provNombre = externasSeleccionadas[0].proveedor_nombre;
      const docs = externasSeleccionadas.map((f) => f.numero_documento || f.concepto || String(f.id)).join(", ");

      // mbf.monto se guarda SIEMPRE en la moneda NATIVA del movimiento bancario (convención que el
      // ledger asume: Math.abs(movimiento.monto)), NUNCA el monto de la factura — clave para el neteo
      // cross-currency (factura USD pagada con banco CLP). Cuando N facturas comparten un movimiento
      // se reparte el monto del banco proporcionalmente al monto_total de cada factura, de modo que la
      // Σ de las N filas = monto del banco (el ledger hace montoPorPagoMBF[mb] = Σ mbf.monto). Para
      // N=1 colapsa a Math.abs(movimiento.monto) exacto.
      const totalFacturas = externasSeleccionadas.reduce((s, f) => s + Number(f.monto_total), 0);
      const montoBanco = Math.abs(Number(movimiento.monto || 0));
      const { error: errMBF } = await client.from("movimiento_bancario_facturas").insert(
        externasSeleccionadas.map((f) => ({
          movimiento_bancario_id: movimiento.id,
          factura_tipo: "factura_externa",
          factura_id: f.id,
          monto: totalFacturas > 0 ? montoBanco * (Number(f.monto_total) / totalFacturas) : montoBanco,
        }))
      );
      if (errMBF) throw errMBF;

      for (const f of externasSeleccionadas) {
        const { error } = await client
          .from("facturas_externas")
          .update({ movimiento_bancario_id: movimiento.id, estado: "pagada" })
          .eq("id", f.id);
        if (error) throw error;
      }

      await actualizarMatch(movimiento.id, {
        match_entidad_tipo: "proveedor",
        match_entidad_id: provId,
        match_entidad_nombre: provNombre,
        match_factura_tipo: "factura_externa",
        match_factura_id: externasSeleccionadas.length === 1 ? externasSeleccionadas[0].id : null,
        match_confianza: 1,
        match_razon: "factura_externa_manual",
      });

      const nuevoCaja = await crearMovimiento({
        pais,
        tipo: "pago",
        fecha: movimiento.fecha,
        monto: Number(movimiento.monto),
        monto_neto: Number(movimiento.monto),
        monto_impuesto: 0,
        moneda: movimiento.moneda,
        estado: "confirmado",
        descripcion: `${provNombre} — ${docs}`,
        entidad_tipo: "proveedor",
        entidad_id: provId,
        entidad_nombre: provNombre,
        categoria_pago: "pago_proveedor",
        cuenta_er: externasSeleccionadas[0].cuenta_er || null,
        origen: "clay",
        movimiento_bancario_id: movimiento.id,
        registrado_por: usuarioId || null,
      });
      if (!nuevoCaja?.id) throw new Error("No se pudo crear el movimiento en caja");

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: nuevoCaja.id,
        procesado_por: usuarioId || null,
      });

      toast.success(
        externasSeleccionadas.length === 1 ? "Factura vinculada" : `${externasSeleccionadas.length} facturas vinculadas`
      );

      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: nuevoCaja.id,
        movimiento_caja: { categoria_pago: "pago_proveedor" },
        match_factura_tipo: "factura_externa",
        match_entidad_nombre: provNombre,
        factura_externa: externasSeleccionadas.map((f) => ({ numero_documento: f.numero_documento || null })),
        // Update optimista del Set de dedup en FlujoCajaV2: hace desaparecer las facturas
        // recién vinculadas del panel "Por pagar" sin esperar un reload completo.
        _externasVinculadas: externasSeleccionadas.map((f) => f.id),
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo "Pago institucional": INSERT en movimientos_caja sin factura asociada.
  // Sin INSERT en movimiento_bancario_facturas (no hay factura). cuenta_er solo para banco.
  const handleConfirmarInstitucional = async () => {
    const inst = INSTITUCIONES.find((i) => i.value === piInstitucion);
    if (!inst) return toast.error("Seleccioná una institución");
    if (inst.requierePeriodo && (!piPeriodoMes || !piPeriodoAño)) {
      return toast.error(`${inst.label} requiere período (mes y año)`);
    }
    setLoading(true);
    try {
      // Idempotencia
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const periodoLabel =
          inst.requierePeriodo && piPeriodoMes && piPeriodoAño ? ` — ${MESES[piPeriodoMes - 1]} ${piPeriodoAño}` : "";
        const descripcion = `${inst.label}${periodoLabel}${piNotas.trim() ? ` (${piNotas.trim()})` : ""}`;
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion,
          entidad_tipo: "otro",
          entidad_id: null,
          entidad_nombre: inst.label,
          categoria_pago: inst.categoria_pago,
          cuenta_er: inst.cuenta_er,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success(`Pago a ${inst.label} registrado`);
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: inst.categoria_pago },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo "Pago a edificio" (Fase 2). Molde: handleConfirmarInstitucional. Diferencias:
  //  - Multi-edificio: una fila en movimientos_caja por edificio del reparto (Fase 1, ya con
  //    cuadre exacto validado), TODAS colgando del mismo movimiento_bancario_id. La FK escalar
  //    movimientos_bancarios.movimiento_caja_id apunta a la primera fila (representativa), igual
  //    criterio que el modo múltiple de proveedor.
  //  - categoria_pago: "comision_edificio" EXACTO en ambos modos (si no, getEdificiosSaldoPendiente
  //    no resta el pago del saldo del edificio).
  //  - Modo "Contra factura": además vincula la factura SII recibida del emisor por el mismo
  //    mecanismo que proveedor single (FK factura_sii_recibida_id + factura 'vinculada' +
  //    puente movimiento_bancario_facturas). Modo "A cuenta": sin factura.
  // Fase 3: el pago a edificio es SIEMPRE a cuenta (amortiza el saldo del edificio). La factura
  // SII es un ADJUNTO OPCIONAL de soporte: si se eligió una, se cuelga del pago por el puente
  // movimiento_bancario_facturas, sin marcarla 'vinculada' ni setear el FK escalar del bancario
  // (no es una vinculación factura↔proveedor formal, solo soporte documental del pago a cuenta).
  const handleConfirmarEdificio = async () => {
    if (edificiosAsignaciones.length === 0) return toast.error("Agregá al menos un edificio");
    if (!edificiosCuadra) return toast.error("La suma asignada debe igualar el monto del movimiento");
    if (edificiosAsignaciones.some((a) => Number(a.monto) <= 0)) return toast.error("Cada monto debe ser mayor a 0");
    setLoading(true);
    try {
      // Idempotencia por movimiento_bancario_id. OJO: crea N filas en movimientos_caja,
      // así que NO usamos .maybeSingle() (rompería con ≥2 filas) — chequeamos existencia con limit(1).
      const { data: existentes, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .limit(1);
      if (errSelect) throw errSelect;

      let primeraCajaId = existentes?.[0]?.id || null;
      if (!existentes?.length) {
        const idsCreadas = [];
        for (const a of edificiosAsignaciones) {
          const montoFila = Number(a.monto);
          const movCaja = await crearMovimiento({
            pais,
            tipo: "pago",
            fecha: movimiento.fecha,
            monto: montoFila,
            monto_neto: montoFila,
            monto_impuesto: 0,
            moneda,
            estado: "confirmado",
            descripcion: `Comisión ${a.edificioNombre}`,
            entidad_tipo: "edificio",
            entidad_id: Number(a.edificioId),
            entidad_nombre: a.edificioNombre,
            categoria_pago: "comision_edificio",
            cuenta_er: null,
            origen: "clay",
            movimiento_bancario_id: movimiento.id,
            registrado_por: usuarioId || null,
          });
          if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
          idsCreadas.push(movCaja.id);
        }
        primeraCajaId = idsCreadas[0];
        await client
          .from("movimientos_bancarios")
          .update({ movimiento_caja_id: primeraCajaId })
          .eq("id", movimiento.id);
      }

      // Adjunto opcional: si se eligió una factura, colgarla por el puente MBF como soporte
      // (idempotente). No marca la factura 'vinculada' ni setea factura_sii_recibida_id.
      if (facturaSeleccionada) {
        const { data: mbfExistente } = await client
          .from("movimiento_bancario_facturas")
          .select("id")
          .eq("movimiento_bancario_id", movimiento.id)
          .eq("factura_tipo", "sii_recibida")
          .eq("factura_id", facturaSeleccionada.id)
          .maybeSingle();
        if (!mbfExistente) {
          await client.from("movimiento_bancario_facturas").insert({
            movimiento_bancario_id: movimiento.id,
            factura_tipo: "sii_recibida",
            factura_id: facturaSeleccionada.id,
            monto: Math.abs(Number(movimiento.monto || 0)),
          });
        }
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: primeraCajaId,
        procesado_por: usuarioId || null,
      });

      const nEd = edificiosAsignaciones.length;
      toast.success(`Pago a ${nEd} edificio${nEd > 1 ? "s" : ""} registrado`);

      // El pago amortiza el saldo del edificio (crédito categoria_pago='comision_edificio').
      // No hay señal de factura que dispare el recálculo selectivo en el padre, así que
      // onSuccess() sin payload cae en el branch !id de handleVincularSuccess → cargarDatos()
      // completo, que recomputa edificiosSaldo. (No tocamos FlujoCajaV2.)
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Path "Compra de activo" (D.2, molde: handleConfirmarEdificio). Vincula el egreso a un activo
  // planificado: crea 1 fila de caja con activo_id, marca el bancario procesado y ejecuta el
  // activo (estado='activo', fecha_compra = fecha del movimiento). NO toca ER (regla 10.2).
  const handleConfirmarActivo = async () => {
    const activo = activosCapex.find((a) => a.id === activoCapexSel);
    if (!activo) return toast.error("Seleccioná un activo planificado");
    setLoading(true);
    try {
      // Idempotencia por movimiento_bancario_id (1 fila de caja).
      const { data: existentes, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .limit(1);
      if (errSelect) throw errSelect;

      let cajaId = existentes?.[0]?.id || null;
      if (!existentes?.length) {
        const montoMov = Math.abs(Number(movimiento.monto || 0));
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto: montoMov,
          monto_neto: montoMov,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: `Compra activo: ${activo.descripcion || activo.tipo_activo} — ${activo.edificioNombre}`,
          entidad_tipo: "edificio",
          entidad_id: activo.destino_id != null ? Number(activo.destino_id) : null,
          entidad_nombre: activo.edificioNombre || null,
          categoria_pago: "compra_activo",
          cuenta_er: null,
          origen: movimiento.origen || "clay",
          movimiento_bancario_id: movimiento.id,
          activo_id: activo.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        cajaId = movCaja.id;
        await client.from("movimientos_bancarios").update({ movimiento_caja_id: cajaId }).eq("id", movimiento.id);
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: cajaId,
        procesado_por: usuarioId || null,
      });

      // Ejecuta el activo: pasa a 'activo' con la fecha real del pago (manda para ER) y sale del CapEx.
      await actualizarActivo(activo.id, { estado: "activo", fecha_compra: movimiento.fecha });

      toast.success("Activo ejecutado y vinculado al pago");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Error al vincular activo: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarCobroACuenta = async () => {
    if (!acCobroProveedorId) return toast.error("Seleccioná un proveedor");
    const categoriaResuelta = acCobroBucket === "factura" ? "cobro_factura" : "cobro_credito";
    setLoading(true);
    try {
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "cobro",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: acCobroNota.trim() || movimiento.descripcion_raw || movimiento.descripcion || "",
          entidad_tipo: "proveedor",
          entidad_id: acCobroProveedorId,
          entidad_nombre: acCobroProveedorNombre,
          categoria_pago: categoriaResuelta,
          cuenta_er: null,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success("Cobro a cuenta registrado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: categoriaResuelta },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Desvincula una fila de movimiento_bancario_facturas. Si era la última, limpia FK directa
  // en movimientos_bancarios, borra el movimiento_caja asociado y devuelve el banco a
  // 'pendiente' — mismo patrón de unwind que handleDesvincularFactoring.
  // Desvincula el PAGO COMPLETO (todas las facturas del movimiento de una vez), nunca de a una
  // factura: desvincular una sola de un pago que agrupa varias (bundle) dejaría las otras huérfanas.
  const handleDesvincularPagoCompleto = async () => {
    // Detección por DATO (no por el flag de modo del modal, que puede no estar seteado al reabrir):
    // si el movimiento tiene un giro en transferencias_internacionales, es un cobro de exportación.
    // El giro es ATÓMICO: desvincular CUALQUIER factura deshace el giro COMPLETO (todas las facturas
    // + costos_intermediacion + la transferencia + caja). No se puede sostener un giro con prorrateo
    // de costos sobre un subconjunto cambiante de facturas. Ver ARQUITECTURA 7.18.
    let girosExport = [];
    try {
      const { data } = await client
        .from("transferencias_internacionales")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id);
      girosExport = data || [];
    } catch {
      girosExport = [];
    }
    const esGiroExport = girosExport.length > 0;

    const mensajeConfirm = esGiroExport
      ? `Este cobro es un giro de exportación que cubre ${vinculacionesSII.length} factura(s). ` +
        "Desvincular deshará el GIRO COMPLETO (todas las facturas, sus costos y la transferencia) y el " +
        "movimiento volverá a estar disponible para revincular. ¿Continuar?"
      : "¿Desvincular el pago completo? El movimiento volverá a pendiente y se liberarán TODAS las facturas vinculadas.";
    if (!window.confirm(mensajeConfirm)) return;

    setLoading(true);
    try {
      // ── Rama EXPORTACIÓN: unwind atómico del giro (hijos→padres). Sin transacción multi-tabla
      // disponible (ver 9.23), así que orden cuidadoso y throw ante el primer error para no dejar
      // un estado intermedio silencioso. Resultado: MBF, caja, costos_intermediacion y
      // transferencias_internacionales quedan SIN filas para este movimiento (foja cero).
      if (esGiroExport) {
        const giroIds = girosExport.map((g) => g.id);

        // 1. costos_intermediacion (hijos del giro)
        const { error: errCostos } = await client.from("costos_intermediacion").delete().in("giro_id", giroIds);
        if (errCostos) throw errCostos;

        // 2. transferencias_internacionales (el giro)
        const { error: errGiro } = await client
          .from("transferencias_internacionales")
          .delete()
          .eq("movimiento_bancario_id", movimiento.id);
        if (errGiro) throw errGiro;

        // 3. TODAS las MBF del movimiento (el giro cubre todas, no solo la factura desvinculada)
        const { error: errMbf } = await client
          .from("movimiento_bancario_facturas")
          .delete()
          .eq("movimiento_bancario_id", movimiento.id);
        if (errMbf) throw errMbf;

        // 4. Caja del movimiento + 5. movimiento → pendiente
        const { data: movActual } = await client
          .from("movimientos_bancarios")
          .select("movimiento_caja_id")
          .eq("id", movimiento.id)
          .single();
        await client
          .from("movimientos_bancarios")
          .update({
            factura_sii_emitida_id: null,
            factura_sii_recibida_id: null,
            movimiento_caja_id: null,
            estado: "pendiente",
            procesado_por: null,
            procesado_at: null,
            match_factura_tipo: null,
            match_factura_id: null,
            match_entidad_tipo: null,
            match_entidad_id: null,
            match_entidad_nombre: null,
            match_confianza: null,
            match_razon: null,
          })
          .eq("id", movimiento.id);
        if (movActual?.movimiento_caja_id) {
          await client.from("movimientos_caja").delete().eq("id", movActual.movimiento_caja_id);
        }
        // Defensa: borrar cualquier caja colgada del movimiento aunque movimiento_caja_id no
        // estuviera seteado (el confirm multi crea la caja con movimiento_bancario_id).
        await client.from("movimientos_caja").delete().eq("movimiento_bancario_id", movimiento.id);

        toast.success("Giro de exportación deshecho");
        onSuccess?.({
          id: movimiento.id,
          estado: "pendiente",
          factura_sii_emitida_id: null,
          factura_sii_recibida_id: null,
          movimiento_caja_id: null,
          procesado_por: null,
          procesado_at: null,
          movimiento_caja: null,
          factura_emitida: null,
          factura_recibida: null,
          factura_externa: [],
          facturas_vinculadas: [],
          match_factura_tipo: null,
          match_factura_id: null,
          match_entidad_tipo: null,
          match_entidad_id: null,
          match_entidad_nombre: null,
        });
        onClose();
        return;
      }

      // ── Rama NO exportación (SII normal / externas): unwind del PAGO COMPLETO ──
      // Se borran TODAS las filas MBF del movimiento de una vez, NUNCA una sola: desvincular de a
      // una rompería un pago que agrupa varias facturas (bundle) → dejaría las otras huérfanas
      // (pagadas apuntando a un banco ya reseteado / caja borrada). Siempre: reset banco + borra
      // caja + libera todas las externas del movimiento.
      const externasDesvinculadasFilas = vinculacionesSII.filter((v) => v.factura_tipo === "factura_externa");
      const idsExternasDesvinculadas = externasDesvinculadasFilas.map((v) => v.factura_id);
      // Montos por factura: para inyectarlos al saldosPorFacturaMap optimista de FlujoCajaV2 (sino
      // el filtro #4 del useMemo descarta la factura aunque salga del Set yaVinculadasExternas).
      const externasDesvinculadasMontos = externasDesvinculadasFilas.map((v) => ({
        id: v.factura_id,
        monto: v.monto,
      }));

      const { data: movActual } = await client
        .from("movimientos_bancarios")
        .select("movimiento_caja_id")
        .eq("id", movimiento.id)
        .single();

      // 1. Borrar TODAS las filas MBF del movimiento.
      const { error: errDel } = await client
        .from("movimiento_bancario_facturas")
        .delete()
        .eq("movimiento_bancario_id", movimiento.id);
      if (errDel) throw errDel;

      // 2. Resetear el banco a pendiente (FKs + match_* limpios; sin esto el badge X re-aparece al
      // recargar porque renderBadgeMovimiento prende por match_factura_tipo).
      await client
        .from("movimientos_bancarios")
        .update({
          factura_sii_emitida_id: null,
          factura_sii_recibida_id: null,
          movimiento_caja_id: null,
          estado: "pendiente",
          procesado_por: null,
          procesado_at: null,
          match_factura_tipo: null,
          match_factura_id: null,
          match_entidad_tipo: null,
          match_entidad_id: null,
          match_entidad_nombre: null,
          match_confianza: null,
          match_razon: null,
        })
        .eq("id", movimiento.id);

      // 3. Borrar la caja del movimiento.
      if (movActual?.movimiento_caja_id) {
        await client.from("movimientos_caja").delete().eq("id", movActual.movimiento_caja_id);
      }

      // 4. Liberar TODAS las facturas externas del movimiento (reaparecen en "Por pagar").
      if (idsExternasDesvinculadas.length > 0) {
        await client
          .from("facturas_externas")
          .update({ movimiento_bancario_id: null, estado: "pendiente" })
          .in("id", idsExternasDesvinculadas);
      }

      toast.success("Pago desvinculado");
      onSuccess?.({
        id: movimiento.id,
        estado: "pendiente",
        factura_sii_emitida_id: null,
        factura_sii_recibida_id: null,
        movimiento_caja_id: null,
        procesado_por: null,
        procesado_at: null,
        movimiento_caja: null,
        factura_emitida: null,
        factura_recibida: null,
        factura_externa: [],
        facturas_vinculadas: [],
        match_factura_tipo: null,
        match_factura_id: null,
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        _externasDesvinculadas: idsExternasDesvinculadas,
        _externasDesvinculadasMontos: externasDesvinculadasMontos,
      });
      onClose();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Desvincular vinculación que vive solo en movimientos_caja (badges G/A/P/D):
  // gasto sin factura, pago/cobro a cuenta, pago institucional, cambio de moneda.
  // No hay MBF ni FK directa a facturas externas/SII — solo caja + estado del banco.
  const handleDesvincularCaja = async () => {
    if (!window.confirm("¿Deshacer esta vinculación?")) return;
    setLoading(true);
    try {
      const movCajaId = movimiento.movimiento_caja_id;

      // D.2: si la vinculación de caja era una compra de activo, revertir el activo a
      // 'planificado' (fecha_compra→null; se conserva fecha_planificada) ANTES de borrar la
      // fila de caja. Reaparece en la proyección CapEx. No toca ER (regla 10.2).
      const { data: cajasActivo } = await client
        .from("movimientos_caja")
        .select("activo_id")
        .eq("movimiento_bancario_id", movimiento.id)
        .not("activo_id", "is", null);
      for (const c of cajasActivo || []) {
        // eslint-disable-next-line no-await-in-loop
        await actualizarActivo(c.activo_id, { estado: "planificado", fecha_compra: null });
      }

      await client
        .from("movimientos_bancarios")
        .update({
          movimiento_caja_id: null,
          estado: "pendiente",
          procesado_por: null,
          procesado_at: null,
          match_entidad_tipo: null,
          match_entidad_id: null,
          match_entidad_nombre: null,
          // Defensa contra datos legacy con match_factura_* huérfanos en el bancario:
          // si quedaran poblados al reload, renderBadgeMovimiento podría prender X.
          match_factura_tipo: null,
          match_factura_id: null,
          match_confianza: null,
          match_razon: null,
        })
        .eq("id", movimiento.id);

      if (movCajaId) {
        await client.from("movimientos_caja").delete().eq("id", movCajaId);
      }

      // Si el movimiento estaba vinculado a una cuota de crédito (path "Préstamo"), revertirla a
      // pendiente para que reaparezca en el panel futuros y en el modal. El filtro por
      // movimiento_bancario_id solo matchea cuotas de crédito → inofensivo para los demás pagos.
      const { data: cuotasRevertidas } = await client
        .from("creditos_cuotas")
        .update({ estado: "pendiente", movimiento_bancario_id: null })
        .eq("movimiento_bancario_id", movimiento.id)
        .select("id");

      toast.success("Vinculación deshecha");
      onSuccess?.({
        id: movimiento.id,
        estado: "pendiente",
        movimiento_caja_id: null,
        movimiento_caja: null,
        match_factura_tipo: null,
        match_factura_id: null,
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        factura_sii_emitida_id: null,
        factura_sii_recibida_id: null,
        factura_emitida: null,
        factura_recibida: null,
        factura_externa: [],
        facturas_vinculadas: [],
        procesado_por: null,
        procesado_at: null,
      });
      // Cuota de crédito revertida → recargar futuros para que la cuota reaparezca en el tab.
      if (cuotasRevertidas?.length) onRefreshFuturos?.();
      onClose();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Desvincular pago a empleado: borra TODAS las filas de pagos_empleados de este
  // movimiento (son componentes del mismo pago). No hay movimientos_caja que limpiar
  // ni estado del banco que tocar — el badge E del padre depende solo de pagos_empleados.
  const handleDesvincularEmpleado = async () => {
    if (!window.confirm("¿Desvincular este pago a empleado del movimiento?")) return;
    setLoading(true);
    try {
      await eliminarPagosPorMovimiento(movimiento.id);
      toast.success("Pago a empleado desvinculado");
      onSuccess?.({
        id: movimiento.id,
        _empleadoDesvinculado: true,
        _movimientoBancarioId: movimiento.id,
      });
      onClose();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleDesvincularFactoring = async () => {
    if (!yaVinculadoFactoring) return;
    if (!window.confirm("¿Desvincular este movimiento del factoring?")) return;
    setLoading(true);
    try {
      const esExcedente = factoringTipoVinc === "excedente";
      const campo = esExcedente ? "movimiento_bancario_remanente_id" : "movimiento_bancario_adelanto_id";
      await actualizarOperacionFactoring(yaVinculadoFactoring.id, { [campo]: null });

      // Fase 2: reversa completa de la caja del remanente creada al vincular. El giro no
      // genera caja, así que solo aplica al excedente.
      if (esExcedente) {
        const { error: errCaja } = await client
          .from("movimientos_caja")
          .delete()
          .eq("movimiento_bancario_id", movimiento.id)
          .eq("categoria_pago", "cobro_factura");
        if (errCaja) throw errCaja;
      }

      await client
        .from("movimientos_bancarios")
        .update({ estado: "pendiente", movimiento_caja_id: null, procesado_por: null, procesado_at: null })
        .eq("id", movimiento.id);
      toast.success("Movimiento desvinculado del factoring");
      onSuccess?.({
        id: movimiento.id,
        estado: "pendiente",
        procesado_por: null,
        procesado_at: null,
        _desvinculaFactoring: true,
      });
      onClose();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarFactoring = async () => {
    if (factoringOpIds.length === 0) return;
    setLoading(true);
    try {
      const esExcedente = factoringTipoVinc === "excedente";
      const campo = esExcedente ? "movimiento_bancario_remanente_id" : "movimiento_bancario_adelanto_id";
      await Promise.all(factoringOpIds.map((id) => actualizarOperacionFactoring(id, { [campo]: movimiento.id })));

      // Fase 2: al vincular el depósito del REMANENTE, su movimiento de caja nace acá con
      // respaldo bancario desde el arranque (cumple regla 10.29 sin deuda; la liquidación
      // ya no crea caja). Solo el caso 1 op ↔ 1 movimiento (el dominante). El multi-op
      // (1 mov → N ops) queda pendiente de decisión de reparto — hoy no existe en datos
      // reales. El giro (anticipo) no genera caja.
      let movCajaId = null;
      if (esExcedente && factoringOpIds.length === 1) {
        const opExc = operacionesFactoring.find((op) => op.id === factoringOpIds[0]);
        // Idempotencia por movimiento_bancario_id (mismo patrón que gasto sin factura).
        const { data: existente, error: errSelect } = await client
          .from("movimientos_caja")
          .select("id")
          .eq("movimiento_bancario_id", movimiento.id)
          .maybeSingle();
        if (errSelect) throw errSelect;
        if (existente) {
          movCajaId = existente.id;
        } else {
          const movCaja = await crearMovimiento({
            pais,
            tipo: "cobro",
            fecha: movimiento.fecha,
            monto: Number(movimiento.monto),
            moneda: movimiento.moneda || moneda,
            estado: "confirmado",
            descripcion: `Remanente factoring — Folio ${opExc?.factura?.folio || opExc?.factura_sii_emitida_id || ""}`,
            entidad_tipo: "cliente",
            entidad_id: opExc?.factura?.cliente_id || null,
            categoria_pago: "cobro_factura",
            origen: "manual",
            movimiento_bancario_id: movimiento.id,
            registrado_por: usuarioId || null,
          });
          if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja del remanente");
          movCajaId = movCaja.id;
        }
      }

      const confirmArgs = { procesado_por: usuarioId };
      if (movCajaId) confirmArgs.movimiento_caja_id = movCajaId;
      await confirmarMovimiento(movimiento.id, confirmArgs);
      toast.success(
        factoringTipoVinc === "giro"
          ? factoringOpIds.length > 1
            ? `${factoringOpIds.length} giros vinculados`
            : "Giro de factoring vinculado"
          : factoringOpIds.length > 1
            ? `${factoringOpIds.length} excedentes vinculados`
            : "Excedente de factoring vinculado"
      );
      // Op completa (con embed proveedor/factura/giro_a_recibir/monto_remanente) para que
      // mapaFactoring del padre arme el tooltip del badge F. Si hay N ops, pasamos la primera —
      // el caso multi-op (mismo mov compartido por varias ops) se refresca al próximo cargarDatos.
      const opSeleccionada = operacionesFactoring.find((op) => op.id === factoringOpIds[0]);
      const tipoVincLabel = factoringTipoVinc === "giro" ? "Giro" : "Excedente";
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId,
        procesado_at: new Date().toISOString(),
        _factoringOp: opSeleccionada,
        _factoringTipo: tipoVincLabel,
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarPrestamo = async () => {
    if (!creditoPrestamo || !cuotaPrestamo) return toast.error("Seleccioná un crédito y una cuota");
    setLoading(true);
    try {
      const { cajaId } = await vincularMovimientoACuota({
        movimiento,
        credito: creditoPrestamo,
        cuota: cuotaPrestamo,
        pais,
        userId: usuarioId,
      });
      toast.success(`Cuota ${cuotaPrestamo.numero_cuota} vinculada — ${creditoPrestamo.nombre}`);
      // onSuccess actualiza el movimiento histórico (badge P). La cuota proyectada en el panel
      // futuros se alimenta de getCuotasPendientesProyeccion → hay que recargar futuros para que
      // la cuota recién pagada desaparezca del tab "Créditos"/"Todo".
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: cajaId,
        movimiento_caja: { categoria_pago: "pago_banco" },
      });
      onRefreshFuturos?.();
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    if (esFactoring) return handleConfirmarFactoring();
    if (esCajaChica) return handleConfirmarCajaChica();
    if (esEmpleado) return handleConfirmarEmpleado();
    if (esPagoExterna) {
      return modoExterno === "existente" ? handleConfirmarVincularExternas() : facturaExt.handleSubmit();
    }
    if (esPagoSinFactura) return handleConfirmarSinFactura();
    if (esPagoACuenta) return handleConfirmarACuenta();
    if (esCobroACuenta) return handleConfirmarCobroACuenta();
    if (esPagoInstitucional) return handleConfirmarInstitucional();
    if (esPrestamo) return handleConfirmarPrestamo();
    // Intercepta ANTES del path de facturaSeleccionada: en modo "Contra factura" hay factura
    // elegida, pero la consume handleConfirmarEdificio (entidad edificio), no el path proveedor.
    if (esPagoEdificio) return handleConfirmarEdificio();
    if (esPagoActivo) return handleConfirmarActivo();
    if (esModoMultiple) return handleConfirmarMultiple();
    if (!facturaSeleccionada) return toast.error("Seleccioná una factura");
    if (!entidadResueltaId && !esCobro) {
      return toast.error("La factura no tiene proveedor asociado. Seleccioná uno en el listado.");
    }
    // Validación de cuadre: monto del movimiento vs monto de la factura. Para facturas
    // parcialmente pagadas (N movimientos → 1 factura), facturaSeleccionada.monto_total ya es
    // el saldo restante (lo ajusta cargarFacturas). Tolerancia: 1% relativa o $1 absoluto (el
    // mayor), para cubrir redondeos y drift cambiario menor. NO aplica con cuota (su monto
    // puede diferir del movimiento por diseño).
    if (!cuotaSeleccionada) {
      const montoMov = Math.abs(Number(movimiento.monto || 0));
      const montoFac = Math.abs(Number(facturaSeleccionada.monto_total || 0));
      // Cross-currency: el movimiento puede estar en otra moneda que la factura (caso Santander:
      // débito USD contra factura CLP). La factura SII recibida siempre está en moneda local;
      // la emitida puede ser USD (DTE 110). Convertir montoMov a la moneda de la factura para comparar.
      const monedaFac = esCobro ? facturaSeleccionada.moneda || moneda : pais === "Perú" ? "PEN" : "CLP";
      const monedaMov = movimiento.moneda || moneda;
      const esCrossCurrency = monedaMov !== monedaFac;
      const montoMovComparable = esCrossCurrency
        ? convertirAMonedaLocal(montoMov, monedaMov, pais, movimiento.fecha)
        : montoMov;
      // Solo bloquear sobrepago: un movimiento ≤ factura es un pago parcial válido (N→1).
      const exceso = montoMovComparable - montoFac;
      // Cross-currency: tolerancia 5% (drift de TC). Same-currency: 1% o $1 (el mayor).
      const tolerancia = esCrossCurrency ? montoFac * 0.05 : Math.max(montoFac * 0.01, 1);
      // Exceso sin DTE (Gastos OUR o propina): el sobrepago es esperado, así que no se bloquea
      // ni se pide confirmación por baja cobertura.
      if (exceso > tolerancia && !excesoSinDte) {
        return toast.error(
          `El monto del movimiento (${formatAmount(montoMovComparable, monedaFac)}) excede la factura (${formatAmount(
            montoFac,
            monedaFac
          )}). Exceso: ${formatAmount(exceso, monedaFac)}`
        );
      }
      // Guardrail: si el movimiento cubre menos del 50% de la factura, pedir confirmación
      // explícita. No bloquea — solo frena el click accidental. Para facturas "parcial"
      // (N→1 en curso), monto_total ya es el remanente, así que el ratio sube y no salta.
      const cobertura = montoFac > 0 ? montoMovComparable / montoFac : 1;
      if (cobertura < 0.5 && !excesoSinDte) {
        const pct = Math.round(cobertura * 100);
        const ok = window.confirm(
          `Este movimiento (${formatAmount(montoMovComparable, monedaFac)}) cubre solo el ${pct}% de la factura (${formatAmount(montoFac, monedaFac)}).\n\n¿Confirmar como pago parcial?`
        );
        if (!ok) return;
      }
    }
    // Guardrails OUR: si los Gastos OUR parecen excesivos o la fecha de la factura está
    // desalineada, pedir confirmación explícita (no bloquea — frena la factura equivocada).
    if (esComisionTransferenciaInternacional && (warnOURExcesivo || warnFechaDesalineada)) {
      const pct = Math.round(ourPorcentaje * 100);
      const partes = [];
      if (warnOURExcesivo) {
        partes.push(`Los Gastos OUR representan el ${pct}% del débito, lo cual parece excesivo.`);
      }
      if (warnFechaDesalineada) {
        partes.push(
          `La fecha de la factura (${facturaSeleccionada.fecha_emision}) difiere del débito (${movimiento.fecha}) por más de 1 día.`
        );
      }
      const ok = window.confirm(`${partes.join("\n\n")}\n\n¿Confirmar de todos modos?`);
      if (!ok) return;
    }
    // Guardrail blando propina: si la propina supera el 13% de la factura, pedir confirmación
    // (no bloquea). Base = factura (a diferencia de OUR, que mide sobre el débito).
    if (esPropina && propinaPorcentaje > 0.13) {
      const ok = window.confirm(
        `La propina (${formatAmount(montoPropina, moneda)}) supera el 13% de la factura (${formatAmount(
          Number(facturaSeleccionada.monto_total || 0),
          moneda
        )}) — ¿factura correcta?\n\n¿Confirmar de todos modos?`
      );
      if (!ok) return;
    }
    setLoading(true);
    try {
      // 1. Si la entidad fue elegida manualmente (auto-match de sii-sync había fallado),
      //    se persiste en la factura SII para que próximas consultas la encuentren.
      if (entidadFallbackId) {
        const tabla = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
        const campo = esCobro ? "cliente_id" : "proveedor_id";
        const { error: errEntidad } = await client
          .from(tabla)
          .update({ [campo]: entidadFallbackId })
          .eq("id", facturaSeleccionada.id);
        if (errEntidad) throw errEntidad;
      }

      // 2. UPDATE movimientos_bancarios (vinculación + procesado)
      const updateMov = {
        ...(esCobro
          ? { factura_sii_emitida_id: facturaSeleccionada.id }
          : { factura_sii_recibida_id: facturaSeleccionada.id }),
        estado: "procesado",
        procesado_por: usuarioId || null,
        procesado_at: new Date().toISOString(),
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        match_factura_tipo: null,
        match_factura_id: null,
        match_confianza: null,
        match_razon: null,
      };
      if (cuotaSeleccionada) {
        Object.assign(
          updateMov,
          esCobro ? { plan_cobro_cuota_id: cuotaSeleccionada.id } : { plan_pago_cuota_id: cuotaSeleccionada.id }
        );
      }
      await client.from("movimientos_bancarios").update(updateMov).eq("id", movimiento.id);

      // 3. Marcar factura SII (fecha cobro emitida / plazo pago recibida)
      if (esCobro) {
        await client
          .from("facturas_sii_emitidas")
          .update({ facturafechacobro: movimiento.fecha })
          .eq("id", facturaSeleccionada.id);
      }

      if (cuotaSeleccionada) {
        const fechaReal = movimiento.fecha;
        const cambios = esCobro
          ? { estado: "cobrado", fecha_cobro_real: fechaReal, movimiento_bancario_id: movimiento.id }
          : { estado: "pagado", fecha_pago_real: fechaReal, movimiento_bancario_id: movimiento.id };
        await (esCobro
          ? actualizarCuotaCobro(cuotaSeleccionada.id, cambios)
          : actualizarCuotaPago(cuotaSeleccionada.id, cambios));
      } else if (!esCobro) {
        await client
          .from("facturas_sii_recibidas")
          .update({ plazopago: movimiento.fecha })
          .eq("id", facturaSeleccionada.id);
      }

      // 4. Marcar la factura como vinculada (siempre, con o sin cuota). Otros módulos
      //    consultan estado_vinculacion='vinculada' para filtrar las ya procesadas.
      await client
        .from(esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas")
        .update({
          estado_vinculacion: "vinculada",
          vinculado_por: usuarioId || null,
          vinculado_at: new Date().toISOString(),
        })
        .eq("id", facturaSeleccionada.id);

      // 4b. Replicar a tabla 1:N facturas_sii_vinculaciones (solo emitidas con venta legacy
      //     ya populada). Sin esto el modal de DTE muestra la factura como pendiente.
      if (esCobro && facturaSeleccionada.venta_tipo && facturaSeleccionada.venta_ref_id != null) {
        await client.from("facturas_sii_vinculaciones").upsert(
          {
            factura_sii_emitida_id: facturaSeleccionada.id,
            venta_tipo: facturaSeleccionada.venta_tipo,
            venta_ref_id: facturaSeleccionada.venta_ref_id,
            venta_ref_index: facturaSeleccionada.venta_ref_index ?? null,
            vinculado_por: usuarioId || null,
            vinculado_at: new Date().toISOString(),
          },
          { onConflict: "factura_sii_emitida_id,venta_tipo,venta_ref_id" }
        );
      }

      // 5. Opción A: crear el asiento en movimientos_caja si no existe (idempotente por movimiento_bancario_id).
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaIdSingle = existente?.id || null;
      if (!existente) {
        const entidadNombre = esCobro
          ? facturaSeleccionada.razon_social_receptor
          : facturaSeleccionada.razon_social_emisor;
        // Breadcrumb de propina: la fila de caja guarda el débito completo (sin cambio estructural,
        // cuenta_er sigue null — regla 7.4); el sufijo documenta el exceso sin DTE incluido.
        const propinaIncluida = Math.abs(Number(movimiento.monto || 0)) - Number(facturaSeleccionada.monto_total || 0);
        const sufijoPropina = esPropina ? ` · incl. propina ${formatAmount(propinaIncluida, moneda)}` : "";
        const movCaja = await crearMovimiento({
          pais,
          tipo: esCobro ? "cobro" : "pago",
          fecha: movimiento.fecha,
          monto: Math.abs(Number(movimiento.monto || 0)),
          monto_neto: facturaSeleccionada.monto_neto,
          monto_impuesto: facturaSeleccionada.monto_iva,
          moneda: movimiento.moneda || (pais === "Chile" ? "CLP" : "PEN"),
          estado: "confirmado",
          descripcion: `${entidadNombre} — Folio ${facturaSeleccionada.folio}${sufijoPropina}`,
          entidad_tipo: esCobro ? "cliente" : "proveedor",
          entidad_id: entidadResueltaId,
          entidad_nombre: entidadNombre,
          categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor",
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (movCaja?.id) {
          await client.from("movimientos_bancarios").update({ movimiento_caja_id: movCaja.id }).eq("id", movimiento.id);
          movCajaIdSingle = movCaja.id;
        }
      } else if (!esCobro) {
        // Formalización de un pago a cuenta preexistente contra una factura SII recibida: el asiento
        // de caja ya existía (bucket 'pago_factura'/'pago_credito' → badge A en el histórico). Al
        // vincularlo, reclasificar a 'pago_proveedor' para que el badge pase de A a C (Compra SII).
        // Solo recibidas (!esCobro); NO toca el motor de imputación del ledger (el MBF ya salda la
        // factura). Ver 4.2 (badges) / 4.9.2 (Vincular a factura).
        await client.from("movimientos_caja").update({ categoria_pago: "pago_proveedor" }).eq("id", existente.id);
      }

      // 6. Registro adicional en movimiento_bancario_facturas (consistencia con modo
      //    múltiple). Idempotente: skip si ya existe vinculación para este par.
      const { data: mbfExistente } = await client
        .from("movimiento_bancario_facturas")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .eq("factura_tipo", esCobro ? "sii_emitida" : "sii_recibida")
        .eq("factura_id", facturaSeleccionada.id)
        .maybeSingle();
      if (!mbfExistente) {
        // Exceso sin DTE (OUR o propina): el ledger acumula Σ mbf.monto y lo compara contra
        // factura.monto_total. Si guardáramos el débito ($107.790) en vez del monto de la factura
        // ($98.857), la factura quedaría con un "crédito de más" (el exceso sin DTE) que distorsiona
        // el FIFO. La caja sí guarda el débito real (paso 5). Ver 10.67 / 10.79.
        await client.from("movimiento_bancario_facturas").insert({
          movimiento_bancario_id: movimiento.id,
          factura_tipo: esCobro ? "sii_emitida" : "sii_recibida",
          factura_id: facturaSeleccionada.id,
          monto: excesoSinDte ? Number(facturaSeleccionada.monto_total || 0) : Math.abs(Number(movimiento.monto || 0)),
        });
      }

      toast.success("Movimiento vinculado correctamente");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaIdSingle,
        movimiento_caja: { categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor" },
        ...(esCobro
          ? {
              factura_sii_emitida_id: facturaSeleccionada.id,
              factura_emitida: {
                folio: facturaSeleccionada.folio,
                razon_social_receptor: facturaSeleccionada.razon_social_receptor,
              },
            }
          : {
              factura_sii_recibida_id: facturaSeleccionada.id,
              factura_recibida: {
                folio: facturaSeleccionada.folio,
                razon_social_emisor: facturaSeleccionada.razon_social_emisor,
              },
            }),
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const facturasFiltradas = facturas.filter((f) => {
    if (esCobroExportacion && f.tipo_dte !== 110) return false;
    if (esCobro && modoCobro === "sii" && f.tipo_dte === 110) return false;
    // NCs recibidas (tipo_dte=61) reducen deuda, no son facturas pagables → fuera del dropdown de pago.
    if (!esCobro && f.tipo_dte === 61) return false;
    // "Mostrar DTE AA" apagado (default): ocultar DTEs de años anteriores al actual. Sin fecha_emision
    // → se conserva. Con el checkbox en true no hay corte por año.
    if (!mostrarDteAnioAnterior && f.fecha_emision && parseInt(f.fecha_emision.substring(0, 4), 10) < añoActualPI)
      return false;
    if (!busqueda) return true;
    const texto = busqueda.trim().toLowerCase();
    const nombre = esCobro
      ? (f.razon_social_receptor || "").toLowerCase()
      : (f.razon_social_emisor || "").toLowerCase();
    if (nombre.includes(texto)) return true;
    if (String(f.folio).includes(texto)) return true;
    // Monto: comparar dígitos del query (descartando . , espacios) contra el monto sin separadores.
    // Para exportación (DTE 110) el monto visible es monto_usd, así que se matchea contra ambos para
    // que la factura se encuentre escriba el usuario el valor USD o el CLP fiscal.
    const queryDigitos = texto.replace(/\D/g, "");
    if (queryDigitos && String(f.monto_total).includes(queryDigitos)) return true;
    if (queryDigitos && f.monto_usd != null && String(f.monto_usd).includes(queryDigitos)) return true;
    return false;
  });

  // Presentación del monto de una factura SII en las listas de cobro. Para exportación (DTE 110) la
  // verdad económica es monto_usd (snapshot de la venta en dólares); monto_total es el CLP fiscal del
  // SII. Con monto_usd → US$; sin él → CLP honesto. Nunca monto_total con etiqueta US$.
  const formatMontoFacturaSII = (f, monedaFallback) => {
    if (esCobro && f.tipo_dte === 110) {
      return f.monto_usd != null ? formatAmount(f.monto_usd, "USD") : formatAmount(f.monto_total, "CLP");
    }
    return formatAmount(f.monto_total, monedaFallback);
  };

  // Modo "Pago a edificio" — adjunto opcional: la lista de facturas de soporte se acota a los
  // proveedores vinculados a los edificios elegidos (edificios.proveedor_id). Comparación por ID
  // (regla 8). Si ningún edificio elegido tiene proveedor vinculado, el Set queda vacío → no se
  // muestran facturas (no exponemos facturas de otros proveedores).
  const proveedorIdsEdificioSel = new Set(edificiosAsignaciones.map((a) => a.proveedorId).filter((id) => id != null));
  const facturasFiltradasEdificio = facturasFiltradas.filter(
    (f) => f.proveedor_id != null && proveedorIdsEdificioSel.has(Number(f.proveedor_id))
  );

  const empleadosFiltrados = empleados.filter((e) => {
    if (!empleadoBusqueda) return true;
    const texto = empleadoBusqueda.toLowerCase();
    const full = `${e.nombre || ""} ${e.apellido || ""}`.toLowerCase();
    return full.includes(texto);
  });

  // Datos derivados del panel "Vinculación actual" → Sección C (pago a empleado).
  const empVinc = pagosEmpleadoVinculados[0]?.empleado;
  const empleadoVinculadoNombre = empVinc ? `${empVinc.nombre || ""} ${empVinc.apellido || ""}`.trim() : "—";
  const totalPagosEmpleadoVinculados = pagosEmpleadoVinculados.reduce((s, p) => s + Number(p.monto || 0), 0);

  // Filtro de facturas externas pendientes para el modo "Vincular existente". Solo se muestran las
  // que tienen SALDO FIFO pendiente (presentes en saldosExtMap); las totalmente amortizadas por pagos
  // a cuenta quedan fuera. Cada fila lleva su `saldoDisplay` (saldo pendiente, NO monto_total) y el
  // flag `_parcial` (saldoDisplay < monto_total) que bloquea su vinculación en el confirm. Oldest-first.
  const extFiltradas = facturasExtPendientes
    .filter((f) => saldosExtMap.has(f.id))
    .map((f) => {
      const saldoDisplay = saldosExtMap.get(f.id);
      return { ...f, saldoDisplay, _parcial: saldoDisplay < Number(f.monto_total) - 0.01 };
    })
    // "Mostrar DTE AA" apagado (default): ocultar DTEs de años anteriores al actual (sin fecha → conservar).
    .filter(
      (f) => mostrarDteAnioAnterior || !f.fecha_emision || parseInt(f.fecha_emision.substring(0, 4), 10) >= añoActualPI
    )
    .filter((f) => {
      if (!busquedaExt) return true;
      const texto = busquedaExt.trim().toLowerCase();
      if ((f.proveedor_nombre || "").toLowerCase().includes(texto)) return true;
      if ((f.numero_documento || "").toLowerCase().includes(texto)) return true;
      if ((f.concepto || "").toLowerCase().includes(texto)) return true;
      const queryDigitos = texto.replace(/\D/g, "");
      if (queryDigitos && String(f.monto_total).includes(queryDigitos)) return true;
      return false;
    })
    .sort((a, b) => (a.fecha_emision || "").localeCompare(b.fecha_emision || ""));

  const monedaLocal = pais === "Perú" ? "PEN" : "CLP";
  const sumaExtSeleccionadas = externasSeleccionadas.reduce((s, f) => s + Number(f.monto_total || 0), 0);
  const monedaExtSeleccionada = externasSeleccionadas[0]?.moneda || null;
  // Cross-currency: el banco está en una moneda (ej: CLP) y las facturas en otra (ej: USD).
  const extCrossCurrency = !!monedaExtSeleccionada && monedaExtSeleccionada !== moneda;
  // Solo podemos convertir cuando el banco está en moneda LOCAL (CLP/PEN), porque
  // convertirAMonedaLocal convierte HACIA la moneda local usando el TC de fecha_emision.
  // Si el banco está en USD/UF (caso raro), no hay destino común → bypass manual.
  const puedeConvertirCrossCurrency = extCrossCurrency && moneda === monedaLocal;
  const extBypassManual = extCrossCurrency && !puedeConvertirCrossCurrency;
  // Suma comparable: cross-currency convertible → convertida a moneda del banco con TC
  // histórico de cada factura; same-currency → suma cruda.
  const convertirFacturaExt = (f) => convertirAMonedaLocal(Number(f.monto_total || 0), f.moneda, pais, f.fecha_emision);
  const sumaExtConvertida = puedeConvertirCrossCurrency
    ? externasSeleccionadas.reduce((s, f) => s + convertirFacturaExt(f), 0)
    : sumaExtSeleccionadas;
  // TC efectivo (local por unidad extranjera) — exacto en single, ponderado en múltiple.
  const tcEfectivoExt = sumaExtSeleccionadas > 0 ? sumaExtConvertida / sumaExtSeleccionadas : 0;
  const diferenciaExt = movimiento?.monto
    ? Math.abs(Number(movimiento.monto) - sumaExtConvertida) / Number(movimiento.monto)
    : 0;
  // Tolerancia sensible a la moneda (ver constantes arriba): 15% cross-currency convertible (spread
  // cambiario del banco), 1% same-currency. Cross-currency no convertible (banco USD/UF) → bypass
  // manual sin validación numérica.
  const extCuadra = extBypassManual
    ? true
    : puedeConvertirCrossCurrency
      ? diferenciaExt < TOLERANCIA_EXT_CROSS_CURRENCY
      : diferenciaExt < TOLERANCIA_EXT_SAME_CURRENCY;

  // Strings del aviso cross-currency precomputados (evita template literals multilínea en JSX).
  const tcLabelExt = tcEfectivoExt.toLocaleString("es-CL", { maximumFractionDigits: 2 });
  const difPctExt = (diferenciaExt * 100).toFixed(1);
  const sumaExtOrigFmt = formatAmount(sumaExtSeleccionadas, monedaExtSeleccionada || moneda);
  const sumaExtConvFmt = formatAmount(sumaExtConvertida, moneda);
  const avisoCrossCurrency = extBypassManual
    ? `Monedas distintas (${moneda} / ${monedaExtSeleccionada}) — cuadre manual`
    : `${sumaExtOrigFmt} → ${sumaExtConvFmt} (TC ${tcLabelExt}) — dif. ${difPctExt}%`;

  const tabs = [
    { key: "cobro", label: "Cobro de cliente" },
    { key: "pago", label: "Pago a proveedor" },
    ...(esEgreso && puedePagarEmpleado ? [{ key: "empleado", label: "Pago a empleado" }] : []),
    ...(esEgreso ? [{ key: "institucional", label: "Pago institucional" }] : []),
    ...(esEgreso ? [{ key: "edificio", label: "Pago a edificio" }] : []),
    ...(esEgreso ? [{ key: "activo", label: "Compra de activo" }] : []),
    ...(esEgreso && creditosDisponibles.length > 0 ? [{ key: "prestamo", label: "Préstamo" }] : []),
    ...(!esEgreso ? [{ key: "factoring", label: "Factoring" }] : []),
  ];

  const mostrarListaFacturas =
    !esEmpleado &&
    !esPagoExterna &&
    !esPagoSinFactura &&
    !esPagoACuenta &&
    !esCobroACuenta &&
    !esPagoInstitucional &&
    // En modo edificio la lista compartida NO se muestra arriba: la factura es adjunto opcional
    // y vive en su propio acordeón (filtrado por proveedor del edificio) dentro del bloque reparto.
    !esPagoEdificio &&
    !esPagoActivo &&
    !esFactoring &&
    !esPrestamo &&
    !esCajaChica;

  const sumaSeleccionadas = operacionesFactoring
    .filter((op) => factoringOpIds.includes(op.id))
    .reduce((acc, op) => {
      const m = factoringTipoVinc === "giro" ? Number(op.giro_a_recibir || 0) : Number(op.monto_remanente || 0);
      return acc + m;
    }, 0);

  const diffAbs = Math.abs(sumaSeleccionadas - monto);
  const diffPct = monto > 0 ? diffAbs / monto : 0;
  const matchExacto = diffAbs === 0;
  const matchOk = diffPct <= 0.01;
  const matchWarning = diffPct > 0.01 && diffPct <= 0.05;
  const matchError = diffPct > 0.05;

  const confirmarDisabled =
    loading ||
    (esPrestamo
      ? !cuotaPrestamo
      : esCajaChica
        ? !empleadoId
        : esFactoring
          ? factoringOpIds.length === 0 || matchError
          : esEmpleado
            ? !empleadoId || !cuadra || componentes.length === 0
            : esPagoExterna
              ? modoExterno === "existente"
                ? externasSeleccionadas.length === 0 || !extCuadra
                : !facturaExt.isValid
              : esPagoSinFactura
                ? !sfProveedorId || !sfConcepto.trim() || !sfCuentaER || sfProveedorEsExtranjero
                : esPagoACuenta
                  ? !acProveedorId || !acBucket
                  : esCobroACuenta
                    ? !acCobroProveedorId || !acCobroBucket
                    : esPagoEdificio
                      ? edificioConfirmarDisabled
                      : esPagoActivo
                        ? !activoCapexSel
                        : esPagoInstitucional
                          ? !piInstitucion ||
                            (!!INSTITUCIONES.find((i) => i.value === piInstitucion)?.requierePeriodo &&
                              (!piPeriodoMes || !piPeriodoAño))
                          : esModoMultiple
                            ? esCobroExportacion
                              ? !costosGiroValidos
                              : !sumaCuadraExacto
                            : !facturaSeleccionada || (!esCobro && !entidadResueltaId));

  if (!movimiento) return null;

  return (
    <Modal show={show} onHide={onClose} size="lg" dialogClassName={esEgreso ? "vinc-mov-ancho" : undefined} scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Vincular movimiento bancario</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: "0.85rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>{movimiento.descripcion_raw}</div>
            <div style={{ color: "#888", fontSize: "0.78rem" }}>{movimiento.fecha}</div>
          </div>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: movimiento.tipo === "ingreso" ? "#27ae60" : "#c0392b",
            }}
          >
            {formatAmount(monto, moneda)}
          </div>
        </div>

        {(vinculacionesSII.length > 0 || tieneVinculacionCaja || pagosEmpleadoVinculados.length > 0) && (
          <div
            style={{
              padding: "12px 14px",
              background: "#fff3cd",
              border: "1px solid #f0d878",
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            <div style={{ fontWeight: 700, color: "#856404", marginBottom: 8, fontSize: "0.85rem" }}>
              Vinculación actual
            </div>

            {/* Sección A: filas de movimiento_bancario_facturas (V/C/X). Etiqueta humana
                según factura_tipo y, si el embed lo trae, folio/doc en vez del ID crudo. */}
            {vinculacionesSII.map((v) => {
              const tipoLabel =
                v.factura_tipo === "sii_emitida"
                  ? "Factura emitida"
                  : v.factura_tipo === "sii_recibida"
                    ? "Factura recibida"
                    : v.factura_tipo === "factura_externa"
                      ? "Factura externa"
                      : v.factura_tipo;
              // El embed enriquecido (v.factura) trae el folio aun en cobros multi-factura, donde
              // movimiento.factura_emitida es null (FKs directas en NULL). Se prefiere "Folio X".
              const identificador = v.factura?.folio
                ? `Folio ${v.factura.folio}`
                : v.factura_tipo === "sii_emitida"
                  ? movimiento?.factura_emitida?.folio
                  : v.factura_tipo === "sii_recibida"
                    ? movimiento?.factura_recibida?.folio
                    : v.factura_tipo === "factura_externa"
                      ? movimiento?.factura_externa?.[0]?.numero_documento
                      : null;
              return (
                <div
                  key={v.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 0",
                    borderTop: "1px solid #f0d878",
                    fontSize: "0.82rem",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>{tipoLabel}</span>
                    <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                    <span>{identificador || `ID ${v.factura_id}`}</span>
                    <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                    <span style={{ fontWeight: 600 }}>
                      {/* Display en moneda real: DTE 110 export → US$ (monto_usd); resto → moneda del
                          movimiento. NO se toca mbf.monto (correcto para la cobertura). */}
                      {v.factura ? formatMontoFacturaSII(v.factura, moneda) : formatAmount(v.monto, moneda)}
                    </span>
                  </div>
                </div>
              );
            })}
            {/* Un ÚNICO botón de desvincular por PAGO (no por factura): libera todas las facturas del
                movimiento de golpe. Evita el camino peligroso de desvincular una sola de un bundle. */}
            {vinculacionesSII.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  paddingTop: 8,
                  marginTop: 4,
                  borderTop: "1px solid #f0d878",
                }}
              >
                <button
                  type="button"
                  onClick={handleDesvincularPagoCompleto}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "1px solid #c62828",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.78rem",
                    color: "#c62828",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "..." : "Desvincular pago completo"}
                </button>
              </div>
            )}

            {/* Sección B: vinculación de solo-caja (G/A/P/D). Se suprime cuando ya hay
                MBF para evitar duplicar info — V/C/X exponen la caja vía sección A. */}
            {tieneVinculacionCaja && vinculacionesSII.length === 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 0",
                  borderTop: "1px solid #f0d878",
                  fontSize: "0.82rem",
                }}
              >
                <div>
                  <span style={{ fontWeight: 600 }}>
                    {categoriaCajaActual === "caja_chica"
                      ? "Caja chica"
                      : categoriaCajaActual === "gasto_sin_factura"
                        ? "Gasto sin factura"
                        : categoriaCajaActual === "pago_a_cuenta" ||
                            categoriaCajaActual === "pago_factura" ||
                            categoriaCajaActual === "pago_credito"
                          ? "Pago a cuenta"
                          : categoriaCajaActual === "cobro_a_cuenta" ||
                              categoriaCajaActual === "cobro_factura" ||
                              categoriaCajaActual === "cobro_credito"
                            ? "Cobro a cuenta"
                            : categoriaCajaActual === "costo_financiero"
                              ? "Cambio de moneda"
                              : categoriaCajaActual === "pago_banco"
                                ? "Pago banco"
                                : categoriaCajaActual === "pago_impuesto"
                                  ? "Pago impuesto"
                                  : categoriaCajaActual === "cotizaciones_previsionales"
                                    ? "Previsional"
                                    : categoriaCajaActual === "compra_activo"
                                      ? "Compra de activo"
                                      : categoriaCajaActual}
                  </span>
                  <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                  <span>{entidadCajaActual || "—"}</span>
                  <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                  <span style={{ fontWeight: 600 }}>{formatAmount(monto, moneda)}</span>
                </div>
                <button
                  type="button"
                  onClick={handleDesvincularCaja}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "1px solid #c62828",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.78rem",
                    color: "#c62828",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "..." : "Desvincular"}
                </button>
              </div>
            )}

            {/* Sección C: pago a empleado (badge E). Vive solo en pagos_empleados — no hay
                MBF ni movimientos_caja. Se suprime si hay MBF (no debería coexistir). */}
            {pagosEmpleadoVinculados.length > 0 && vinculacionesSII.length === 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "6px 0",
                  borderTop: "1px solid #f0d878",
                  fontSize: "0.82rem",
                }}
              >
                <div>
                  <div>
                    <span style={{ fontWeight: 600 }}>Pago a empleado</span>
                    <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                    <span>{empleadoVinculadoNombre}</span>
                    <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                    <span style={{ fontWeight: 600 }}>{formatAmount(totalPagosEmpleadoVinculados, moneda)}</span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: "0.74rem", color: "#856404" }}>
                    {pagosEmpleadoVinculados.map((p) => {
                      const tipoCap = p.tipo ? p.tipo.charAt(0).toUpperCase() + p.tipo.slice(1) : "—";
                      const periodo =
                        p.periodo_mes && p.periodo_año ? ` ${MESES[p.periodo_mes - 1]} ${p.periodo_año}` : "";
                      return (
                        <div key={p.id}>
                          {tipoCap}
                          {periodo} · {formatAmount(p.monto, p.moneda || moneda)}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDesvincularEmpleado}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "1px solid #c62828",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.78rem",
                    color: "#c62828",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "..." : "Desvincular"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Transferencia internacional recibida YA vinculada: comisión de recepción neteada
            (Santander la descuenta de los USD entrantes). Sección dentro del flujo "ya vinculado". */}
        {esTransferenciaRecibida && yaEstaVinculado && (
          <div
            style={{
              padding: "12px 14px",
              background: "#fff3cd",
              border: "1px solid #f0d878",
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            <ComisionNeteadaSelector
              key={`ingreso-vinc-${movimiento.id}`}
              titulo="Comisión por recepción (neteada)"
              cargarVinculadas={() => getComisionesIngresoNeteadas(movimiento.id)}
              cargarDisponibles={() => getFacturasComisionIngresoDisponibles(pais)}
              vincular={(facturaId) => vincularComisionIngreso(movimiento.id, facturaId)}
              desvincular={(filaId) => desvincularComisionIngreso(filaId)}
              destacar={(f) =>
                !!f.fecha_emision &&
                !!movimiento?.fecha &&
                Math.abs(new Date(f.fecha_emision) - new Date(movimiento.fecha)) / 86400000 <= 3
              }
              formatAmount={formatAmount}
              formatFecha={(s) => (s ? `${s.substring(8, 10)}/${s.substring(5, 7)}` : "—")}
              moneda={pais === "Perú" ? "PEN" : "CLP"}
              onCambio={onRefreshFuturos}
              mostrarDteAnioAnterior={mostrarDteAnioAnterior}
              setMostrarDteAnioAnterior={setMostrarDteAnioAnterior}
            />
          </div>
        )}

        {!yaEstaVinculado && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
              {tabs.map((t) => {
                // Deshabilitar visualmente los tabs que no aplican al tipo de movimiento.
                // Egreso → no cobro ni factoring; ingreso → no pago/empleado/institucional/edificio.
                const tabDeshabilitado = esEgreso
                  ? t.key === "cobro" || t.key === "factoring"
                  : ["pago", "empleado", "institucional", "edificio", "activo"].includes(t.key);
                return (
                  <button
                    key={t.key}
                    type="button"
                    disabled={tabDeshabilitado}
                    onClick={() => {
                      if (tabDeshabilitado) return;
                      setTipoVinculacion(t.key);
                      setModoEmpleado("remuneracion");
                    }}
                    style={{
                      width: 140,
                      padding: "6px 0",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      cursor: tabDeshabilitado ? "not-allowed" : "pointer",
                      background: tipoVinculacion === t.key ? "#2c3e50" : "transparent",
                      color: tipoVinculacion === t.key ? "#fff" : "#666",
                      opacity: tabDeshabilitado ? 0.4 : 1,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {!esEmpleado && tipoVinculacion === "pago" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
                {[
                  { key: "sii", label: `Factura ${enteFiscal}` },
                  { key: "externa", label: "Factura externa" },
                  { key: "sin_factura", label: "Sin factura" },
                  { key: "a_cuenta", label: "A cuenta" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setModoCompra(m.key)}
                    style={{
                      width: 140,
                      padding: "6px 0",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: modoCompra === m.key ? "#2c3e50" : "transparent",
                      color: modoCompra === m.key ? "#fff" : "#666",
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            {tipoVinculacion === "cobro" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
                {[
                  { key: "sii", label: "SII" },
                  { key: "exportacion", label: "Exportación" },
                  { key: "a_cuenta", label: "A cuenta" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setModoCobro(m.key)}
                    style={{
                      width: 140,
                      padding: "6px 0",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: modoCobro === m.key ? "#2c3e50" : "transparent",
                      color: modoCobro === m.key ? "#fff" : "#666",
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            {esPagoEdificio && (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                  background: "#f8f9fa",
                  borderRadius: 4,
                  padding: "8px 10px",
                  marginBottom: 12,
                }}
              >
                Pago <strong>a cuenta</strong> que amortiza el saldo de comisión del/los edificio(s). Adjuntar una
                factura SII de soporte es <strong>opcional</strong>: si no elegís ninguna, el pago se confirma igual.
              </div>
            )}

            {/* Wizard export: stepper de 3 pasos (solo en modo exportación). Clickeable para navegar
                libre; ✓ en los pasos válidos. El cobro normal no muestra stepper. */}
            {esCobroExportacion && (
              <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                {[
                  { n: 1, label: "Facturas", ok: facturasSeleccionadas.length > 0 },
                  { n: 2, label: "Costos del giro", ok: costosGiroValidos },
                  { n: 3, label: "Comisión Santander", ok: false, opcional: true },
                ].map((s) => (
                  <button
                    key={s.n}
                    type="button"
                    onClick={() => setPasoExport(s.n)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      borderRadius: 6,
                      fontSize: "0.76rem",
                      fontWeight: 600,
                      border: `1px solid ${pasoExport === s.n ? "#2980b9" : "#dee2e6"}`,
                      background: pasoExport === s.n ? "#2980b9" : "transparent",
                      color: pasoExport === s.n ? "#fff" : "#666",
                      cursor: "pointer",
                    }}
                  >
                    {s.ok ? "✓ " : ""}
                    {s.n}. {s.label}
                    {s.opcional ? " (opc.)" : ""}
                  </button>
                ))}
              </div>
            )}

            {mostrarListaFacturas && (!esCobroExportacion || pasoExport === 1) && (
              <>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar por nombre, folio o monto..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    style={{ flex: 3 }}
                  />
                  <label
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={mostrarDteAnioAnterior}
                      onChange={(e) => setMostrarDteAnioAnterior(e.target.checked)}
                    />
                    Mostrar DTE AA
                  </label>
                </div>

                <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
                  {loading ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Cargando...</div>
                  ) : facturasFiltradas.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin facturas</div>
                  ) : (
                    facturasFiltradas.map((f) => {
                      const nombre = esCobro ? f.razon_social_receptor : f.razon_social_emisor;
                      const seleccionada = facturasSeleccionadas.some((x) => x.id === f.id);
                      // SII recibidas no tiene columna `moneda` en DB → forzar moneda local del país.
                      // Para emitidas, `f.moneda` puede ser USD (DTE 110 exportación) y se respeta.
                      const monedaFactura = esCobro ? f.moneda || moneda : pais === "Perú" ? "PEN" : "CLP";
                      // Exportación: sin snapshot USD la factura no es cobrable en este flujo USD →
                      // se muestra deshabilitada y no clickeable, con hint para resolverla.
                      const exportSinUsd = esCobroExportacion && f.monto_usd == null;
                      return (
                        <div
                          key={f.id}
                          onClick={exportSinUsd ? undefined : () => handleSeleccionarFactura(f)}
                          style={{
                            padding: "8px 12px",
                            cursor: exportSinUsd ? "not-allowed" : "pointer",
                            opacity: exportSinUsd ? 0.5 : 1,
                            fontSize: "0.83rem",
                            borderBottom: "1px solid #f0f0f0",
                            background: seleccionada ? "#e8f4fd" : "transparent",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <span>{`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`}</span>
                            <span style={{ margin: "0 10px" }}>|</span>
                            <span>{nombre}</span>
                            <span style={{ marginLeft: 8 }}>Folio {f.folio}</span>
                            {f._clienteNombre && (
                              <span style={{ marginLeft: 8, color: "#555" }}>· {f._clienteNombre}</span>
                            )}
                            {f._parcial && (
                              <span style={{ marginLeft: 8, color: "#e67e22", fontSize: "0.72rem", fontWeight: 600 }}>
                                parcial
                              </span>
                            )}
                            {exportSinUsd && (
                              <span style={{ marginLeft: 8, color: "#999", fontSize: "0.72rem", fontStyle: "italic" }}>
                                Sin USD — vinculá la venta primero
                              </span>
                            )}
                          </div>
                          <div style={{ color: esCobro ? "#27ae60" : "#c0392b", fontWeight: 600 }}>
                            {f._parcial && (
                              <span
                                style={{
                                  color: "#999",
                                  fontWeight: 400,
                                  textDecoration: "line-through",
                                  marginRight: 6,
                                }}
                              >
                                {formatAmount(f.monto_total_original, monedaFactura)}
                              </span>
                            )}
                            {formatMontoFacturaSII(f, monedaFactura)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {esModoMultiple && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      borderRadius: 4,
                      background: sumaCuadraExacto ? "#e8f5e9" : "#fff3cd",
                      border: `1px solid ${sumaCuadraExacto ? "#a5d6a7" : "#f0d878"}`,
                    }}
                  >
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 8 }}>
                      Selección múltiple ({facturasSeleccionadas.length} facturas)
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      {facturasSeleccionadas.map((f) => {
                        const nombre = esCobro ? f.razon_social_receptor : f.razon_social_emisor;
                        const monedaFactura = esCobro ? f.moneda || moneda : pais === "Perú" ? "PEN" : "CLP";
                        return (
                          <div
                            key={f.id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "0.82rem",
                              padding: "3px 0",
                            }}
                          >
                            <span>
                              {`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`} | {nombre}{" "}
                              Folio {f.folio}
                            </span>
                            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <span>{formatMontoFacturaSII(f, monedaFactura)}</span>
                              <button
                                type="button"
                                onClick={() => handleSeleccionarFactura(f)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#c0392b",
                                  cursor: "pointer",
                                  fontSize: "1rem",
                                  padding: "0 4px",
                                }}
                                title="Quitar de la selección"
                              >
                                ×
                              </button>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid #ccc",
                        paddingTop: 6,
                        fontSize: "0.82rem",
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: "2px 12px",
                      }}
                    >
                      <span>Suma seleccionada</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>
                        {esCobroExportacion
                          ? formatAmount(sumaUsdSeleccionadas, "USD")
                          : formatAmount(sumaMontosSeleccionados, moneda)}
                      </span>
                      <span>Monto del movimiento</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(monto, moneda)}</span>
                      <span style={{ fontWeight: 600 }}>Diferencia</span>
                      <span
                        style={{
                          textAlign: "right",
                          fontWeight: 700,
                          color: esCobroExportacion
                            ? Math.abs(diferenciaUsd) < 0.5
                              ? "#2e7d32"
                              : "#c0392b"
                            : sumaCuadraExacto
                              ? "#2e7d32"
                              : "#c0392b",
                        }}
                      >
                        {esCobroExportacion
                          ? Math.abs(diferenciaUsd) < 0.5
                            ? "✓ exacta"
                            : formatAmount(diferenciaUsd, "USD")
                          : sumaCuadraExacto
                            ? "✓ exacta"
                            : formatAmount(diferenciaMontos, moneda)}
                      </span>
                    </div>
                  </div>
                )}

                {necesitaSelectorEntidad && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      borderRadius: 4,
                      background: "#fff3cd",
                      border: "1px solid #f0d878",
                    }}
                  >
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 6 }}>
                      La factura no tiene {esCobro ? "cliente" : "proveedor"} asociado — seleccioná uno:
                    </div>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={`Buscar ${esCobro ? "cliente" : "proveedor"}...`}
                      value={entidadFallbackBusqueda}
                      onChange={(e) => setEntidadFallbackBusqueda(e.target.value)}
                      style={{ marginBottom: 6, fontSize: "0.82rem" }}
                    />
                    <div
                      style={{
                        maxHeight: 160,
                        overflowY: "auto",
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                        background: "#fff",
                      }}
                    >
                      {entidadFallbackOptions
                        .filter((opt) => {
                          if (!entidadFallbackBusqueda) return true;
                          const t = entidadFallbackBusqueda.toLowerCase();
                          const nombre = (esCobro ? opt.empresa || opt.nombre : opt.nombre) || "";
                          const rut = opt.rut || opt.rut_ruc || "";
                          return nombre.toLowerCase().includes(t) || rut.toLowerCase().includes(t);
                        })
                        .slice(0, 100)
                        .map((opt) => {
                          const sel = entidadFallbackId === opt.id;
                          const nombre = esCobro ? opt.empresa || opt.nombre : opt.nombre;
                          return (
                            <div
                              key={opt.id}
                              onClick={() => setEntidadFallbackId(opt.id)}
                              style={{
                                padding: "6px 12px",
                                cursor: "pointer",
                                fontSize: "0.82rem",
                                borderBottom: "1px solid #f0f0f0",
                                background: sel ? "#e8f4fd" : "transparent",
                                fontWeight: sel ? 600 : 400,
                              }}
                            >
                              {nombre}
                              {(opt.rut || opt.rut_ruc) && (
                                <span style={{ marginLeft: 8, fontSize: "0.72rem", color: "#888" }}>
                                  {opt.rut || opt.rut_ruc}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>

                    {!esCobro &&
                      (!creandoProvSII ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setProvSIINombre(facturaSeleccionada?.razon_social_emisor || "");
                            setProvSIIRut(facturaSeleccionada?.rut_emisor || "");
                            setCreandoProvSII(true);
                          }}
                          style={{ marginTop: 8, fontSize: "0.78rem" }}
                        >
                          + Nuevo proveedor
                        </Button>
                      ) : (
                        <div
                          style={{
                            marginTop: 8,
                            padding: 10,
                            border: "1px solid #dee2e6",
                            borderRadius: 4,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: "0.72rem", color: "#666", marginBottom: 6 }}>
                            Pre-cargado desde la factura {enteFiscal}. Verificá antes de crear.
                          </div>
                          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Nombre / razón social"
                              value={provSIINombre}
                              onChange={(e) => setProvSIINombre(e.target.value)}
                              style={{ flex: 2, fontSize: "0.82rem" }}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="RUT"
                              value={provSIIRut}
                              onChange={(e) => setProvSIIRut(e.target.value)}
                              style={{ flex: 1, fontSize: "0.82rem" }}
                            />
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={handleCrearProveedorDesdeSII}
                              disabled={!provSIINombre.trim()}
                              style={{ fontSize: "0.78rem" }}
                            >
                              Crear y seleccionar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setCreandoProvSII(false);
                                setProvSIINombre("");
                                setProvSIIRut("");
                              }}
                              style={{ fontSize: "0.78rem" }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ))}

                    {esCobro &&
                      (!creandoClienteSII ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setClienteSIIEmpresa(facturaSeleccionada?.razon_social_receptor || "");
                            setClienteSIIRut(facturaSeleccionada?.rut_receptor || "");
                            setCreandoClienteSII(true);
                          }}
                          style={{ marginTop: 8, fontSize: "0.78rem" }}
                        >
                          + Nuevo cliente
                        </Button>
                      ) : (
                        <div
                          style={{
                            marginTop: 8,
                            padding: 10,
                            border: "1px solid #dee2e6",
                            borderRadius: 4,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: "0.72rem", color: "#666", marginBottom: 6 }}>
                            Pre-cargado desde la factura {enteFiscal}. Verificá antes de crear.
                          </div>
                          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Razón social"
                              value={clienteSIIEmpresa}
                              onChange={(e) => setClienteSIIEmpresa(e.target.value)}
                              style={{ flex: 2, fontSize: "0.82rem" }}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="RUT"
                              value={clienteSIIRut}
                              onChange={(e) => setClienteSIIRut(e.target.value)}
                              style={{ flex: 1, fontSize: "0.82rem" }}
                            />
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={handleCrearClienteDesdeSII}
                              disabled={!clienteSIIEmpresa.trim()}
                              style={{ fontSize: "0.78rem" }}
                            >
                              Crear y seleccionar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setCreandoClienteSII(false);
                                setClienteSIIEmpresa("");
                                setClienteSIIRut("");
                              }}
                              style={{ fontSize: "0.78rem" }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {cuotas.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 6 }}>
                      Plan de {esCobro ? "cobro" : "pago"} — seleccioná la cuota:
                    </div>
                    {cuotas.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => setCuotaSeleccionada(c)}
                        style={{
                          padding: "6px 12px",
                          cursor: "pointer",
                          fontSize: "0.82rem",
                          borderRadius: 4,
                          marginBottom: 4,
                          border: `1px solid ${cuotaSeleccionada?.id === c.id ? "#2c3e50" : "#dee2e6"}`,
                          background: cuotaSeleccionada?.id === c.id ? "#e8f4fd" : "#fff",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>
                          Cuota {c.numero_cuota} · vence {c.fecha_vencimiento}
                        </span>
                        <span style={{ fontWeight: 600 }}>{formatAmount(c.monto_cuota, moneda)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Comisión transferencia internacional: desglose de Gastos OUR (sin DTE). El débito
                    bancario supera la factura SII porque el cobro del banco corresponsal no lleva IVA
                    ni entra al DTE. Panel informativo, no bloqueante. */}
                {mostrarDesgloseOUR && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 12,
                      border: "1px solid #198754",
                      borderRadius: 4,
                      background: "#f0f9f4",
                      fontSize: "0.82rem",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>
                        Factura {enteFiscal} Nº {facturaSeleccionada.folio}
                      </span>
                      <span style={{ fontWeight: 600 }}>{formatAmount(facturaSeleccionada.monto_total, moneda)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>Gastos OUR (sin DTE)</span>
                      <span style={{ fontWeight: 600 }}>
                        {formatAmount(Math.abs(monto) - Number(facturaSeleccionada.monto_total || 0), moneda)}
                      </span>
                    </div>
                    <div style={{ borderTop: "1px solid #198754", margin: "6px 0" }} />
                    <div
                      style={{ display: "flex", justifyContent: "space-between", color: "#198754", fontWeight: 700 }}
                    >
                      <span>Débito bancario ✓</span>
                      <span>{formatAmount(Math.abs(monto), moneda)}</span>
                    </div>
                  </div>
                )}

                {/* Guardrails visuales (no bloquean): factura probablemente equivocada. */}
                {warnOURExcesivo && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "8px 12px",
                      border: "1px solid #e67e22",
                      borderRadius: 4,
                      background: "#fdf3e7",
                      color: "#b35900",
                      fontSize: "0.8rem",
                    }}
                  >
                    ⚠ Gastos OUR parecen excesivos ({Math.round(ourPorcentaje * 100)}%) — ¿es la factura correcta?
                  </div>
                )}
                {warnFechaDesalineada && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "8px 12px",
                      border: "1px solid #e67e22",
                      borderRadius: 4,
                      background: "#fdf3e7",
                      color: "#b35900",
                      fontSize: "0.8rem",
                    }}
                  >
                    ⚠ La factura es del {facturaSeleccionada.fecha_emision} y el débito del {movimiento.fecha} —
                    ¿corresponden?
                  </div>
                )}

                {/* Propina de restorán (sin DTE): toggle para marcar que el exceso del débito sobre la
                    factura SII es propina. Solo visible cuando hay sobrepago real y NO es OUR (rama
                    paralela e independiente, mutuamente excluyente con OUR). */}
                {puedeMarcarPropina && (
                  <label
                    style={{
                      marginTop: 14,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: "0.82rem",
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" checked={esPropina} onChange={(e) => setEsPropina(e.target.checked)} />
                    <span>El excedente es propina (sin factura)</span>
                  </label>
                )}

                {/* Desglose propina (sin DTE): mismo formato que el panel de OUR, panel propio. El débito
                    bancario supera la factura SII por la propina, que no genera documento. No bloqueante. */}
                {mostrarDesglosePropina && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 12,
                      border: "1px solid #198754",
                      borderRadius: 4,
                      background: "#f0f9f4",
                      fontSize: "0.82rem",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>
                        Factura {enteFiscal} Nº {facturaSeleccionada.folio}
                      </span>
                      <span style={{ fontWeight: 600 }}>{formatAmount(facturaSeleccionada.monto_total, moneda)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>Propina (sin DTE)</span>
                      <span style={{ fontWeight: 600 }}>
                        {formatAmount(Math.abs(monto) - Number(facturaSeleccionada.monto_total || 0), moneda)}
                      </span>
                    </div>
                    <div style={{ borderTop: "1px solid #198754", margin: "6px 0" }} />
                    <div
                      style={{ display: "flex", justifyContent: "space-between", color: "#198754", fontWeight: 700 }}
                    >
                      <span>Débito bancario ✓</span>
                      <span>{formatAmount(Math.abs(monto), moneda)}</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {esPagoExterna && (
              <>
                {/* Sub-toggle: vincular factura externa ya cargada vs cargar una nueva. */}
                <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
                  {[
                    { key: "existente", label: "Vincular existente" },
                    { key: "nueva", label: "Crear nueva" },
                  ].map((m) => {
                    const deshabilitado = false;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setModoExterno(m.key)}
                        disabled={deshabilitado}
                        style={{
                          width: 140,
                          padding: "6px 0",
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          border: "1px solid #dee2e6",
                          borderRadius: 6,
                          cursor: deshabilitado ? "not-allowed" : "pointer",
                          background: modoExterno === m.key ? "#2c3e50" : "transparent",
                          color: modoExterno === m.key ? "#fff" : "#666",
                          opacity: deshabilitado ? 0.4 : 1,
                        }}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>

                {modoExterno === "existente" ? (
                  facturasExtPendientes.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999", fontSize: "0.85rem" }}>
                      No hay facturas externas pendientes de vincular
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Buscar por proveedor, doc, concepto o monto..."
                          value={busquedaExt}
                          onChange={(e) => setBusquedaExt(e.target.value)}
                          style={{ flex: 3 }}
                        />
                        <label
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={mostrarDteAnioAnterior}
                            onChange={(e) => setMostrarDteAnioAnterior(e.target.checked)}
                          />
                          Mostrar DTE AA
                        </label>
                      </div>

                      <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
                        {extFiltradas.length === 0 ? (
                          <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin resultados</div>
                        ) : (
                          extFiltradas.map((f) => {
                            const seleccionada = externasSeleccionadas.some((x) => x.id === f.id);
                            const label = f.concepto || f.numero_documento || `Doc ${f.id}`;
                            return (
                              <div
                                key={f.id}
                                onClick={() => handleSeleccionarExterna(f)}
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  fontSize: "0.83rem",
                                  borderBottom: "1px solid #f0f0f0",
                                  background: seleccionada ? "#e8f4fd" : "transparent",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div>
                                  <span>{`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`}</span>
                                  <span style={{ margin: "0 10px" }}>|</span>
                                  <span>{f.proveedor_nombre}</span>
                                  <span style={{ margin: "0 10px", color: "#999" }}>|</span>
                                  <span>{label}</span>
                                  {f._parcial && (
                                    <span
                                      style={{ marginLeft: 8, fontSize: "0.7rem", color: "#e67e22", fontWeight: 600 }}
                                    >
                                      (saldo parcial)
                                    </span>
                                  )}
                                </div>
                                <div style={{ color: "#c0392b", fontWeight: 600 }}>
                                  {formatAmount(f.saldoDisplay, f.moneda || moneda)}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {extCrossCurrency && externasSeleccionadas.length > 0 && (
                        <div
                          style={{
                            marginTop: 10,
                            padding: "6px 10px",
                            fontSize: "0.78rem",
                            color: extBypassManual ? "#856404" : extCuadra ? "#198754" : "#c62828",
                            background: extBypassManual ? "#fff3cd" : extCuadra ? "#e8f5e9" : "#fdecea",
                            border: `1px solid ${extBypassManual ? "#f0d878" : extCuadra ? "#a5d6a7" : "#f5b7b1"}`,
                            borderRadius: 4,
                          }}
                        >
                          {avisoCrossCurrency}
                        </div>
                      )}

                      {externasSeleccionadas.length >= 2 && (
                        <div
                          style={{
                            marginTop: 14,
                            padding: 10,
                            borderRadius: 4,
                            background: extCuadra && !extBypassManual ? "#e8f5e9" : "#fff3cd",
                            border: `1px solid ${extCuadra && !extBypassManual ? "#a5d6a7" : "#f0d878"}`,
                          }}
                        >
                          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 8 }}>
                            Selección múltiple ({externasSeleccionadas.length} facturas)
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            {externasSeleccionadas.map((f) => {
                              const label = f.concepto || f.numero_documento || `Doc ${f.id}`;
                              return (
                                <div
                                  key={f.id}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "0.82rem",
                                    padding: "3px 0",
                                  }}
                                >
                                  <span>
                                    {`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`} | {label}
                                  </span>
                                  <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <span>{formatAmount(f.monto_total, f.moneda || moneda)}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleSeleccionarExterna(f)}
                                      style={{
                                        border: "none",
                                        background: "transparent",
                                        color: "#c0392b",
                                        cursor: "pointer",
                                        fontSize: "1rem",
                                        padding: "0 4px",
                                      }}
                                      title="Quitar de la selección"
                                    >
                                      ×
                                    </button>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          <div
                            style={{
                              borderTop: "1px solid #ccc",
                              paddingTop: 6,
                              fontSize: "0.82rem",
                              display: "grid",
                              gridTemplateColumns: "1fr auto",
                              gap: "2px 12px",
                            }}
                          >
                            <span>Suma seleccionada</span>
                            <span style={{ textAlign: "right", fontWeight: 600 }}>
                              {formatAmount(sumaExtSeleccionadas, monedaExtSeleccionada || moneda)}
                            </span>
                            {puedeConvertirCrossCurrency && (
                              <>
                                <span>Suma convertida (TC {tcLabelExt})</span>
                                <span style={{ textAlign: "right", fontWeight: 600 }}>
                                  {formatAmount(sumaExtConvertida, moneda)}
                                </span>
                              </>
                            )}
                            <span>Monto del movimiento</span>
                            <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(monto, moneda)}</span>
                            <span style={{ fontWeight: 600 }}>Diferencia</span>
                            <span
                              style={{
                                textAlign: "right",
                                fontWeight: 700,
                                color: extBypassManual ? "#856404" : extCuadra ? "#2e7d32" : "#c0392b",
                              }}
                            >
                              {extBypassManual
                                ? "cuadre manual"
                                : extCuadra && !extCrossCurrency
                                  ? "✓ exacta"
                                  : `${(diferenciaExt * 100).toFixed(2)}%`}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )
                ) : (
                  <FacturaExternaForm form={facturaExt} pais={pais} proveedores={proveedores} />
                )}
              </>
            )}

            {esPagoSinFactura && (
              <>
                {sfProveedorEsExtranjero ? (
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#842029",
                      background: "#f8d7da",
                      border: "1px solid #f5c2c7",
                      borderRadius: 4,
                      padding: "10px 12px",
                      marginBottom: 12,
                    }}
                  >
                    ⚠️ <strong>{sfProveedorNombre || "Este proveedor"}</strong> emite factura — no se puede registrar
                    como “Sin factura”. Cargala en la pestaña <strong>“Factura externa”</strong>.
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      background: "#f8f9fa",
                      borderRadius: 4,
                      padding: "8px 10px",
                      marginBottom: 12,
                    }}
                  >
                    Para gastos operativos legítimos sin DTE (café con cliente, taxi, propinas, etc.). Se registra en{" "}
                    <code>movimientos_caja</code> con <code>categoria_pago=&quot;gasto_sin_factura&quot;</code>; sin IVA
                    recuperable.
                  </div>
                )}

                <ProveedorSelector
                  value={sfProveedorId}
                  onChange={(p) => {
                    setSfProveedorId(p.id);
                    setSfProveedorNombre(p.nombre);
                    if (p.cuenta_er_default && !sfCuentaER) setSfCuentaER(p.cuenta_er_default);
                  }}
                  filter={(p) => p.tipo === "comercial" || p.tipo === "interno"}
                  proveedores={proveedores}
                />

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Concepto</div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Ej: Café con cliente DIDI / Taxi a reunión Latam"
                    value={sfConcepto}
                    onChange={(e) => setSfConcepto(e.target.value)}
                    style={{ fontSize: "0.82rem" }}
                  />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
                    Cuenta Estado de Resultados
                  </div>
                  <select
                    className="form-select form-select-sm"
                    value={sfCuentaER}
                    onChange={(e) => setSfCuentaER(e.target.value)}
                    style={{ fontSize: "0.82rem" }}
                  >
                    <option value="">Seleccionar cuenta…</option>
                    {["Costos Fijos", "Costos Variables"].map((grupo) => (
                      <optgroup key={grupo} label={grupo}>
                        {CUENTAS_ER.filter((c) => c.grupo === grupo).map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Comercio (opcional)</div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Ej: Starbucks Magdalena, Uber, Taxi Cabify…"
                    value={sfComercio}
                    onChange={(e) => setSfComercio(e.target.value)}
                    style={{ fontSize: "0.82rem" }}
                  />
                  <div style={{ fontSize: "0.7rem", color: "#888", marginTop: 3 }}>
                    Si lo dejás vacío, se usa el concepto como nombre de entidad.
                  </div>
                </div>
              </>
            )}

            {esPagoACuenta && (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    background: "#f8f9fa",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  Movimiento a cuenta corriente del proveedor (anticipo, retiro, devolución). No es un gasto operativo —
                  afecta solo el saldo del proveedor, no el Estado de Resultados.
                </div>

                <ProveedorSelector
                  value={acProveedorId}
                  onChange={(p) => {
                    setAcProveedorId(p.id);
                    setAcProveedorNombre(p.nombre);
                  }}
                  filter={(p) => p.tipo === "comercial" || p.tipo === "interno"}
                  proveedores={proveedores}
                />

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Nota (opcional)</div>
                  <textarea
                    className="form-control form-control-sm"
                    placeholder="Ej: Anticipo honorarios marzo, retiro contra cuenta personal…"
                    value={acNota}
                    onChange={(e) => setAcNota(e.target.value)}
                    style={{ fontSize: "0.82rem", minHeight: 60 }}
                  />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: 6 }}>
                    ¿A qué cuenta corresponde este pago?
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["factura", "credito"].map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() => setAcBucket(b)}
                        style={{
                          padding: "6px 18px",
                          borderRadius: 4,
                          border: "1px solid #dee2e6",
                          background: acBucket === b ? "#2c3e50" : "#fff",
                          color: acBucket === b ? "#fff" : "#333",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        {b === "factura" ? "Factura" : "Crédito"}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {esCobroACuenta && (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    background: "#f8f9fa",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  Cobro a cuenta corriente del proveedor (devolución, anticipo retornado, refacturación). No es un
                  ingreso operativo — aumenta el saldo a favor del proveedor en el Estado de Cuenta, no el Estado de
                  Resultados.
                </div>

                <ProveedorSelector
                  value={acCobroProveedorId}
                  onChange={(p) => {
                    setAcCobroProveedorId(p.id);
                    setAcCobroProveedorNombre(p.nombre);
                  }}
                  filter={(p) => p.tipo === "comercial" || p.tipo === "interno"}
                  proveedores={proveedores}
                />

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Nota (opcional)</div>
                  <textarea
                    className="form-control form-control-sm"
                    placeholder="Ej: Devolución pago duplicado, anticipo no aplicado…"
                    value={acCobroNota}
                    onChange={(e) => setAcCobroNota(e.target.value)}
                    style={{ fontSize: "0.82rem", minHeight: 60 }}
                  />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: 6 }}>
                    ¿A qué cuenta corresponde este cobro?
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["factura", "credito"].map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() => setAcCobroBucket(b)}
                        style={{
                          padding: "6px 18px",
                          borderRadius: 4,
                          border: "1px solid #dee2e6",
                          background: acCobroBucket === b ? "#2c3e50" : "#fff",
                          color: acCobroBucket === b ? "#fff" : "#333",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        {b === "factura" ? "Factura" : "Crédito"}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {esPagoActivo && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: 8 }}>
                  Elegí el activo planificado que ejecuta este pago
                </div>
                {activosCapex.length === 0 ? (
                  <div style={{ color: "#999", fontSize: "0.85rem" }}>No hay activos planificados en {pais}.</div>
                ) : (
                  <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 6 }}>
                    {activosCapex.map((a) => (
                      <label
                        key={a.id}
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          padding: "8px 12px",
                          borderBottom: "1px solid #f1f1f1",
                          cursor: "pointer",
                          background: activoCapexSel === a.id ? "#eef5ff" : "transparent",
                        }}
                      >
                        <input
                          type="radio"
                          name="activoCapex"
                          checked={activoCapexSel === a.id}
                          onChange={() => setActivoCapexSel(a.id)}
                        />
                        <span style={{ flex: 1, fontSize: "0.82rem" }}>
                          <strong>{a.edificioNombre}</strong> · {a.tipo_activo}
                          {a.descripcion ? ` — ${a.descripcion}` : ""}
                          {a.fecha_planificada ? ` · estimado ${a.fecha_planificada}` : ""}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                          {formatAmount(Number(a.costo_unitario), a.moneda)}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: "0.75rem", color: "#666", marginTop: 6 }}>
                  Al confirmar, el activo pasa a “ejecutado” con fecha de compra = fecha del movimiento y sale de la
                  proyección CapEx.
                </div>
              </div>
            )}

            {esPagoInstitucional &&
              (() => {
                const inst = INSTITUCIONES.find((i) => i.value === piInstitucion);
                const requierePeriodo = !!inst?.requierePeriodo;
                return (
                  <>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#666",
                        background: "#f8f9fa",
                        borderRadius: 4,
                        padding: "8px 10px",
                        marginBottom: 12,
                      }}
                    >
                      Para pagos a entidades sin DTE: PREVIRED, {enteFiscal}, Tesorería, comisiones bancarias, etc. Se
                      registra en <code>movimientos_caja</code> sin factura asociada.
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Institución</div>
                      <select
                        className="form-select form-select-sm"
                        value={piInstitucion}
                        onChange={(e) => setPiInstitucion(e.target.value)}
                        style={{ fontSize: "0.82rem" }}
                      >
                        <option value="">Seleccionar institución…</option>
                        {INSTITUCIONES.map((i) => (
                          <option key={i.value} value={i.value}>
                            {i.label.replace(/\bSII\b/g, enteFiscal)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {requierePeriodo && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Período que cubre</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <select
                            className="form-select form-select-sm"
                            value={piPeriodoMes || ""}
                            onChange={(e) => setPiPeriodoMes(e.target.value ? Number(e.target.value) : null)}
                            style={{ flex: 2, fontSize: "0.82rem" }}
                          >
                            <option value="">Mes…</option>
                            {MESES.map((m, i) => (
                              <option key={i} value={i + 1}>
                                {m}
                              </option>
                            ))}
                          </select>
                          <select
                            className="form-select form-select-sm"
                            value={piPeriodoAño || ""}
                            onChange={(e) => setPiPeriodoAño(e.target.value ? Number(e.target.value) : null)}
                            style={{ flex: 1, fontSize: "0.82rem" }}
                          >
                            {añosPI.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Notas (opcional)</div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Ej: Folio 12345, ajuste retroactivo, etc."
                        value={piNotas}
                        onChange={(e) => setPiNotas(e.target.value)}
                        style={{ fontSize: "0.82rem" }}
                      />
                    </div>
                  </>
                );
              })()}

            {esPrestamo &&
              (() => {
                const hoyStr = new Date().toISOString().slice(0, 10);
                const fmtFecha = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : "—");
                const cuotasCred = creditoPrestamo?.cuotas || [];
                const montoMov = Math.abs(monto);
                const montoCuota = cuotaPrestamo ? Number(cuotaPrestamo.monto_total || 0) : null;
                const diff = montoCuota != null ? Math.abs(montoMov - montoCuota) : 0;
                const diffPct = montoCuota ? diff / montoCuota : 0;
                const monedaCred = creditoPrestamo?.moneda || moneda;
                return (
                  <>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#666",
                        background: "#f8f9fa",
                        borderRadius: 4,
                        padding: "8px 10px",
                        marginBottom: 12,
                      }}
                    >
                      Vinculá este egreso a una cuota pendiente de un crédito. Se registra en{" "}
                      <code>movimientos_caja</code> como <code>pago_banco</code> y la cuota queda marcada como pagada.
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Crédito</div>
                      <select
                        className="form-select form-select-sm"
                        value={creditoPrestamo?.id || ""}
                        onChange={(e) => {
                          const c = creditosDisponibles.find((x) => String(x.id) === e.target.value) || null;
                          setCreditoPrestamo(c);
                          setCuotaPrestamo(null);
                        }}
                        style={{ fontSize: "0.82rem" }}
                      >
                        <option value="">Seleccionar crédito…</option>
                        {creditosDisponibles.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre} · {c.moneda} ({c.cuotas.length} pendientes)
                          </option>
                        ))}
                      </select>
                    </div>

                    {creditoPrestamo && (
                      <div>
                        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Cuota a pagar</div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
                          <thead>
                            <tr style={{ background: "#212529", color: "#fff" }}>
                              <th style={{ padding: "4px 8px", textAlign: "center" }}>N°</th>
                              <th style={{ padding: "4px 8px", textAlign: "center" }}>Vence</th>
                              <th style={{ padding: "4px 8px", textAlign: "right" }}>Monto</th>
                              <th style={{ padding: "4px 8px", width: 36 }} />
                            </tr>
                          </thead>
                          <tbody>
                            {cuotasCred.map((q) => {
                              const vencida = q.fecha_vencimiento < hoyStr;
                              const sel = cuotaPrestamo?.id === q.id;
                              return (
                                <tr
                                  key={q.id}
                                  onClick={() => setCuotaPrestamo(q)}
                                  style={{
                                    cursor: "pointer",
                                    background: sel ? "#e8f4fd" : "#fff",
                                    borderBottom: "1px solid #dee2e6",
                                  }}
                                >
                                  <td style={{ padding: "4px 8px", textAlign: "center" }}>{q.numero_cuota}</td>
                                  <td
                                    style={{
                                      padding: "4px 8px",
                                      textAlign: "center",
                                      color: vencida ? "#c0392b" : "inherit",
                                      fontWeight: vencida ? 700 : 400,
                                    }}
                                  >
                                    {fmtFecha(q.fecha_vencimiento)}
                                  </td>
                                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                                    {formatAmount(q.monto_total, monedaCred)}
                                  </td>
                                  <td style={{ padding: "4px 8px", textAlign: "center" }}>
                                    <input type="radio" checked={sel} onChange={() => setCuotaPrestamo(q)} />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {cuotaPrestamo && diff >= 2 && (
                          <div
                            style={{
                              marginTop: 10,
                              fontSize: "0.76rem",
                              borderRadius: 4,
                              padding: "8px 10px",
                              background: diffPct >= 0.1 ? "#f8d7da" : "#fff3cd",
                              color: diffPct >= 0.1 ? "#842029" : "#856404",
                              border: `1px solid ${diffPct >= 0.1 ? "#f5c2c7" : "#ffecb5"}`,
                            }}
                          >
                            {diffPct >= 0.1
                              ? "Diferencia significativa: "
                              : "El monto del movimiento difiere de la cuota: "}
                            movimiento {formatAmount(montoMov, monedaCred)} vs cuota{" "}
                            {formatAmount(montoCuota, monedaCred)}.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

            {esPagoEdificio && (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    background: "#f8f9fa",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  Repartí el monto del movimiento entre los edificios; la suma debe cuadrar exacto. El saldo pendiente
                  que se muestra al lado es de referencia (comisión generada − pagada).
                </div>

                {/* Selector + Agregar */}
                <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
                  <select
                    className="form-select form-select-sm"
                    value={edificioSelectorId}
                    onChange={(e) => setEdificioSelectorId(e.target.value)}
                    style={{ flex: 1, fontSize: "0.82rem" }}
                  >
                    <option value="">Seleccionar edificio…</option>
                    {edificiosSaldo
                      .filter((ed) => !edificiosAsignaciones.some((a) => a.edificioId === ed.edificioId))
                      .map((ed) => (
                        <option key={ed.edificioId} value={ed.edificioId}>
                          {ed.edificioNombre} — saldo {formatAmount(ed.saldoPendiente, ed.moneda || moneda)}
                        </option>
                      ))}
                  </select>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={agregarEdificioAsignacion}
                    disabled={!edificioSelectorId}
                  >
                    + Agregar
                  </Button>
                </div>

                {/* Filas de asignación por edificio */}
                {edificiosAsignaciones.length === 0 ? (
                  <div
                    style={{
                      padding: 16,
                      textAlign: "center",
                      color: "#999",
                      fontSize: "0.82rem",
                      border: "1px dashed #dee2e6",
                      borderRadius: 4,
                    }}
                  >
                    Agregá al menos un edificio para repartir el monto.
                  </div>
                ) : (
                  edificiosAsignaciones.map((a) => {
                    const ed = edificiosSaldo.find((e) => e.edificioId === a.edificioId);
                    const saldoRef = ed?.saldoPendiente || 0;
                    // Sanity-check blando: si lo asignado supera el saldo pendiente conocido (>0),
                    // se advierte pero NO se bloquea (sobrepagos legítimos: anticipos, cta. corriente).
                    const montoAsignado = Number(a.monto) || 0;
                    const excedeSaldo = saldoRef > 0 && montoAsignado > saldoRef;
                    return (
                      <div key={a.edificioId} style={{ marginBottom: 8 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            border: `1px solid ${excedeSaldo ? "#f0d878" : "#dee2e6"}`,
                            borderRadius: 4,
                            padding: 10,
                            background: excedeSaldo ? "#fff8e6" : "#fafbfc",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: "0.83rem",
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {a.edificioNombre}
                            </div>
                            <div style={{ fontSize: "0.72rem", color: "#888" }}>
                              Saldo pendiente: {formatAmount(saldoRef, ed?.moneda || moneda)}
                            </div>
                          </div>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="Monto"
                            value={a.monto}
                            onChange={(e) => actualizarEdificioAsignacion(a.edificioId, e.target.value)}
                            style={{ flex: "0 0 140px", fontSize: "0.82rem", textAlign: "right" }}
                          />
                          <button
                            type="button"
                            onClick={() => quitarEdificioAsignacion(a.edificioId)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#c0392b",
                              cursor: "pointer",
                              fontSize: "1rem",
                              padding: "0 4px",
                            }}
                            title="Quitar edificio"
                          >
                            ×
                          </button>
                        </div>
                        {excedeSaldo && (
                          <div style={{ fontSize: "0.72rem", color: "#a06a00", padding: "3px 2px 0" }}>
                            ⚠ Asignado {formatAmount(montoAsignado, ed?.moneda || moneda)} supera el saldo pendiente{" "}
                            {formatAmount(saldoRef, ed?.moneda || moneda)} — ¿pago adelantado?
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {/* Línea de cuadre: Asignado X / Total movimiento Y + restante */}
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    borderRadius: 4,
                    background: edificiosCuadra ? "#e8f5e9" : "#fdecea",
                    fontSize: "0.82rem",
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 600,
                  }}
                >
                  <span>
                    Asignado {formatAmount(totalEdificiosAsignado, moneda)} / {formatAmount(montoAbsMovimiento, moneda)}
                  </span>
                  <span>
                    {edificiosCuadra ? (
                      "✓ Cuadra"
                    ) : (
                      <span style={{ color: "#c0392b" }}>restante {formatAmount(restanteEdificios, moneda)}</span>
                    )}
                  </span>
                </div>

                {/* Acordeón: adjuntar factura de soporte (opcional). Cerrado por defecto.
                    La lista solo se renderiza si está abierto, y se acota al proveedor del edificio. */}
                <div style={{ marginTop: 14, borderTop: "1px solid #eee", paddingTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => setMostrarFacturaEdificio((v) => !v)}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      color: "#2c3e50",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: "0.7rem" }}>{mostrarFacturaEdificio ? "▾" : "▸"}</span>
                    Adjuntar factura de soporte (opcional)
                    {facturaSeleccionada && (
                      <span style={{ color: "#198754", fontWeight: 500, fontSize: "0.78rem" }}>
                        — Folio {facturaSeleccionada.folio} adjunta
                      </span>
                    )}
                  </button>

                  {mostrarFacturaEdificio && (
                    <div style={{ marginTop: 10 }}>
                      {proveedorIdsEdificioSel.size === 0 ? (
                        <div style={{ padding: 12, textAlign: "center", color: "#999", fontSize: "0.8rem" }}>
                          Elegí un edificio con proveedor vinculado para ver sus facturas.
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Buscar por folio o monto..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            style={{ marginBottom: 8, fontSize: "0.82rem" }}
                          />
                          <div
                            style={{
                              maxHeight: 200,
                              overflowY: "auto",
                              border: "1px solid #dee2e6",
                              borderRadius: 4,
                            }}
                          >
                            {facturasFiltradasEdificio.length === 0 ? (
                              <div style={{ padding: 16, textAlign: "center", color: "#999", fontSize: "0.8rem" }}>
                                Sin facturas del proveedor de este edificio
                              </div>
                            ) : (
                              facturasFiltradasEdificio.map((f) => {
                                const sel = facturaSeleccionada?.id === f.id;
                                return (
                                  <div
                                    key={f.id}
                                    onClick={() => setFacturaSeleccionada(sel ? null : f)}
                                    style={{
                                      padding: "8px 12px",
                                      cursor: "pointer",
                                      fontSize: "0.82rem",
                                      borderBottom: "1px solid #f0f0f0",
                                      background: sel ? "#e8f4fd" : "transparent",
                                      fontWeight: sel ? 600 : 400,
                                      display: "flex",
                                      justifyContent: "space-between",
                                      gap: 8,
                                    }}
                                  >
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {sel ? "✓ " : ""}
                                      Folio {f.folio} — {f.razon_social_emisor}
                                    </span>
                                    <span style={{ whiteSpace: "nowrap", color: "#555" }}>
                                      {formatAmount(f.monto_total, pais === "Perú" ? "PEN" : "CLP")}
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {esEmpleado && !yaEstaVinculado && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
                {[
                  { key: "remuneracion", label: "Remuneración" },
                  { key: "caja_chica", label: "Caja Chica" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setModoEmpleado(m.key)}
                    style={{
                      width: 140,
                      padding: "6px 0",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: modoEmpleado === m.key ? "#2c3e50" : "transparent",
                      color: modoEmpleado === m.key ? "#fff" : "#666",
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            {esEmpleado && (
              <>
                {esCajaChica && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      background: "#f8f9fa",
                      borderRadius: 4,
                      padding: "8px 10px",
                      marginBottom: 12,
                    }}
                  >
                    Entrega de fondos de caja chica a un empleado. Se registra en <code>movimientos_caja</code> con{" "}
                    <code>categoria_pago=&quot;caja_chica&quot;</code>; aparece como cargo (DEBE) en su estado de cuenta
                    de caja chica.
                  </div>
                )}
                <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 6 }}>Empleado</div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar empleado..."
                  value={empleadoBusqueda}
                  onChange={(e) => setEmpleadoBusqueda(e.target.value)}
                  style={{ marginBottom: 8 }}
                />
                <div
                  style={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    marginBottom: 16,
                  }}
                >
                  {loading && empleados.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Cargando...</div>
                  ) : empleadosFiltrados.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin empleados</div>
                  ) : (
                    empleadosFiltrados.map((e) => {
                      const seleccionado = empleadoId === e.id;
                      return (
                        <div
                          key={e.id}
                          onClick={() => setEmpleadoId(e.id)}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontSize: "0.83rem",
                            borderBottom: "1px solid #f0f0f0",
                            background: seleccionado ? "#e8f4fd" : "transparent",
                            fontWeight: seleccionado ? 600 : 400,
                          }}
                        >
                          {e.nombre} {e.apellido}
                          {e._esEgresado && (
                            <span style={{ color: "#999", fontSize: "0.85em", marginLeft: 6 }}>(Egresado)</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}

            {esRemuneracion && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>Desglose del pago</div>
                  <Button size="sm" variant="outline-secondary" onClick={agregarComponente}>
                    + Componente
                  </Button>
                </div>

                {componentes.map((c, idx) => {
                  const requierePeriodo = TIPOS_REQUIEREN_PERIODO.includes(c.tipo);
                  return (
                    <div
                      key={idx}
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                        padding: 10,
                        marginBottom: 8,
                        background: "#fafbfc",
                      }}
                    >
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                        <select
                          className="form-select form-select-sm"
                          value={c.tipo}
                          onChange={(ev) => actualizarComponente(idx, "tipo", ev.target.value)}
                          style={{ flex: "0 0 130px", fontSize: "0.82rem" }}
                        >
                          {TIPOS_PAGO_EMPLEADO.map((t) => (
                            <option key={t} value={t}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </option>
                          ))}
                        </select>
                        {requierePeriodo ? (
                          <>
                            <select
                              className="form-select form-select-sm"
                              value={c.periodo_mes || ""}
                              onChange={(ev) =>
                                actualizarComponente(
                                  idx,
                                  "periodo_mes",
                                  ev.target.value ? Number(ev.target.value) : null
                                )
                              }
                              style={{ flex: "0 0 120px", fontSize: "0.82rem" }}
                            >
                              <option value="">Mes…</option>
                              {MESES.map((m, i) => (
                                <option key={i} value={i + 1}>
                                  {m}
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              placeholder="Año"
                              value={c.periodo_año || ""}
                              onChange={(ev) =>
                                actualizarComponente(
                                  idx,
                                  "periodo_año",
                                  ev.target.value ? Number(ev.target.value) : null
                                )
                              }
                              style={{ flex: "0 0 80px", fontSize: "0.82rem" }}
                            />
                          </>
                        ) : (
                          <span style={{ flex: 1, fontSize: "0.78rem", color: "#888" }}>(sin período)</span>
                        )}
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Monto"
                          value={c.monto}
                          onChange={(ev) => actualizarComponente(idx, "monto", ev.target.value)}
                          style={{ flex: 1, fontSize: "0.82rem", textAlign: "right" }}
                        />
                        {componentes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => eliminarComponente(idx)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#c0392b",
                              cursor: "pointer",
                              fontSize: "1rem",
                              padding: "0 4px",
                            }}
                            title="Eliminar componente"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Notas (opcional)"
                        value={c.notas}
                        onChange={(ev) => actualizarComponente(idx, "notas", ev.target.value)}
                        style={{ fontSize: "0.78rem" }}
                      />
                    </div>
                  );
                })}

                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    borderRadius: 4,
                    background: cuadra ? "#e8f5e9" : "#fdecea",
                    fontSize: "0.82rem",
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 600,
                  }}
                >
                  <span>Total componentes</span>
                  <span>
                    {formatAmount(totalComponentes, moneda)}{" "}
                    {!cuadra && (
                      <span style={{ color: "#c0392b", marginLeft: 8 }}>
                        (diferencia {formatAmount(diferencia, moneda)})
                      </span>
                    )}
                  </span>
                </div>
              </>
            )}

            {esFactoring && yaVinculadoFactoring && (
              <div
                style={{
                  padding: "16px",
                  background: "#f0f3f5",
                  borderRadius: 8,
                  border: "2px solid #8e44ad",
                  marginBottom: 12,
                }}
              >
                <div style={{ fontWeight: 700, color: "#8e44ad", marginBottom: 8 }}>
                  ✓ Movimiento ya vinculado a Factoring
                </div>
                <div style={{ fontSize: "0.85rem", color: "#333", marginBottom: 4 }}>
                  <strong>Cliente:</strong> {yaVinculadoFactoring.factura?.razon_social_receptor || "—"}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#333", marginBottom: 4 }}>
                  <strong>Folio:</strong> {yaVinculadoFactoring.factura?.folio || "—"}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#333", marginBottom: 4 }}>
                  <strong>Tipo:</strong> {factoringTipoVinc === "giro" ? "Giro (anticipo)" : "Excedente"}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#333", marginBottom: 16 }}>
                  <strong>Proveedor:</strong> {yaVinculadoFactoring.proveedor?.nombre || "—"}
                </div>
                <button
                  type="button"
                  onClick={handleDesvincularFactoring}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "1px solid #c62828",
                    borderRadius: 4,
                    padding: "6px 16px",
                    fontSize: "0.82rem",
                    color: "#c62828",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Desvinculando..." : "Desvincular"}
                </button>
              </div>
            )}

            {esFactoring && !yaVinculadoFactoring && (
              <div style={{ padding: "8px 0" }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 6 }}>
                    Tipo de vinculación
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { key: "giro", label: "Giro (anticipo)" },
                      { key: "excedente", label: "Excedente" },
                    ].map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => {
                          setFactoringTipoVinc(t.key);
                          setFactoringOpIds([]);
                        }}
                        style={{
                          padding: "6px 16px",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                          background: factoringTipoVinc === t.key ? "#2c3e50" : "#e9ecef",
                          color: factoringTipoVinc === t.key ? "#fff" : "#333",
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 6 }}>
                  {factoringTipoVinc === "giro"
                    ? "Operaciones con giro pendiente de cobro"
                    : "Operaciones con excedente pendiente de cobro"}
                </label>

                {(() => {
                  const candidatas = operacionesFactoring.filter((op) => {
                    const montoOp =
                      factoringTipoVinc === "giro" ? Number(op.giro_a_recibir || 0) : Number(op.monto_remanente || 0);
                    const pendiente =
                      factoringTipoVinc === "giro"
                        ? !op.movimiento_bancario_adelanto_id
                        : !op.movimiento_bancario_remanente_id;
                    return pendiente && montoOp > 0;
                  });
                  if (candidatas.length === 0) {
                    return (
                      <div style={{ color: "#999", fontSize: "0.82rem", padding: "12px 0" }}>
                        Sin operaciones pendientes. Verificá el tipo de vinculación.
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {candidatas.map((op) => {
                        const montoOp = factoringTipoVinc === "giro" ? op.giro_a_recibir : op.monto_remanente;
                        const selected = factoringOpIds.includes(op.id);
                        return (
                          <div
                            key={op.id}
                            onClick={() =>
                              setFactoringOpIds((prev) =>
                                prev.includes(op.id) ? prev.filter((id) => id !== op.id) : [...prev, op.id]
                              )
                            }
                            style={{
                              padding: "10px 14px",
                              borderRadius: 6,
                              border: `2px solid ${selected ? "#2c3e50" : "#dee2e6"}`,
                              background: selected ? "#f0f3f5" : "#fff",
                              cursor: "pointer",
                              fontSize: "0.82rem",
                            }}
                          >
                            <div style={{ fontWeight: 600 }}>
                              {op.factura?.razon_social_receptor || "—"}
                              {" — Folio "}
                              {op.factura?.folio || "—"}
                            </div>
                            <div style={{ color: "#666", marginTop: 2 }}>
                              {op.proveedor?.nombre || "—"}
                              {" · Vence "}
                              {op.fecha_cobro_final || "—"}
                              {" · "}
                              <span style={{ color: "#198754", fontWeight: 700 }}>{formatAmount(montoOp, moneda)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {factoringOpIds.length > 0 && (
                  <div style={{ marginTop: 12, fontSize: "0.82rem" }}>
                    {matchExacto || matchOk ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: 4,
                          background: "#d1e7dd",
                          color: "#0f5132",
                          fontWeight: 600,
                        }}
                      >
                        {matchExacto
                          ? "Match exacto"
                          : `Diferencia: ${formatAmount(diffAbs, moneda)} (${(diffPct * 100).toFixed(2)}%)`}
                      </span>
                    ) : matchWarning ? (
                      <span style={{ color: "#b8730a", fontWeight: 600 }}>
                        Diferencia de {formatAmount(diffAbs, moneda)} ({(diffPct * 100).toFixed(2)}%) — podés continuar
                        pero verificá los montos
                      </span>
                    ) : matchError ? (
                      <span style={{ color: "#c0392b", fontWeight: 600 }}>
                        Diferencia de {formatAmount(diffAbs, moneda)} ({(diffPct * 100).toFixed(2)}%) — no se puede
                        confirmar
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {/* Cobro export (modelo 7.18): costos del giro. Franco copia del SWIFT el monto
                instruido y la comisión del banco internacional; el resto se deriva. El cobro
                export EXIGE estos datos (gate del botón Confirmar). Wizard: Paso 2. */}
            {esCobroExportacion && pasoExport === 2 && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  background: "#f3f7fb",
                  border: "1px solid #cfe0f0",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                    gap: 10,
                  }}
                >
                  <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>Costos del giro</div>
                  <label
                    className="btn btn-sm btn-outline-primary"
                    style={{
                      fontSize: "0.74rem",
                      marginBottom: 0,
                      cursor: cargandoSwift ? "wait" : "pointer",
                      whiteSpace: "nowrap",
                    }}
                    title="Lee un PDF SWIFT (pacs.008) y autocompleta los montos. El cuadre valida igual."
                  >
                    {cargandoSwift ? "Leyendo…" : "📄 Cargar SWIFT (PDF)"}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      style={{ display: "none" }}
                      disabled={cargandoSwift}
                      onChange={handleCargarSwift}
                    />
                  </label>
                </div>

                <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                  <label style={{ flex: 1, fontSize: "0.78rem" }}>
                    Monto instruido (InstdAmt) · US$
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={giroInstdAmt}
                      onChange={(e) => setGiroInstdAmt(e.target.value)}
                      placeholder="0.00"
                      style={{ marginTop: 4 }}
                    />
                  </label>
                  <label style={{ flex: 1, fontSize: "0.78rem" }}>
                    Comisión banco internacional (Citibank) · US$
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={giroCitibank}
                      onChange={(e) => setGiroCitibank(e.target.value)}
                      placeholder="0.00"
                      style={{ marginTop: 4 }}
                    />
                  </label>
                  <label style={{ flex: 1, fontSize: "0.78rem" }}>
                    Monto recibido Santander (IntrBkSttlmAmt) · US$
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={giroSantanderRecibido}
                      onChange={(e) => setGiroSantanderRecibido(e.target.value)}
                      placeholder="(del aviso Santander)"
                      style={{ marginTop: 4 }}
                    />
                  </label>
                </div>

                <button
                  type="button"
                  onClick={() => setGiroSwiftOpen((v) => !v)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#2c3e50",
                    fontSize: "0.76rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    padding: 0,
                    marginBottom: giroSwiftOpen ? 8 : 0,
                  }}
                >
                  {giroSwiftOpen ? "▾" : "▸"} Datos del SWIFT (opcional)
                </button>
                {giroSwiftOpen && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <label style={{ fontSize: "0.76rem" }}>
                      UETR
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={giroUetr}
                        onChange={(e) => setGiroUetr(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </label>
                    <label style={{ fontSize: "0.76rem" }}>
                      Ordenante
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={giroOrdenante}
                        onChange={(e) => setGiroOrdenante(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </label>
                    <label style={{ fontSize: "0.76rem" }}>
                      BIC banco ordenante
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={giroOrdenanteBic}
                        onChange={(e) => setGiroOrdenanteBic(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </label>
                    <label style={{ fontSize: "0.76rem" }}>
                      Fecha valor
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={giroFechaValor}
                        onChange={(e) => setGiroFechaValor(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </label>
                  </div>
                )}

                <div
                  style={{
                    borderTop: "1px solid #cfe0f0",
                    paddingTop: 8,
                    marginTop: 8,
                    fontSize: "0.82rem",
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "3px 12px",
                  }}
                >
                  <span>Facturado (Σ US$ facturas)</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(giroFacturado, "USD")}</span>
                  <span>Comisión Place Exchange</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(giroComisionPlace, "USD")}</span>
                  <span>Comisión bancaria internacional</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(giroCitibankNum, "USD")}</span>
                  <span>Comisión bancaria local (Santander)</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>
                    {formatAmount(giroComisionSantander, "USD")}
                  </span>
                  <span style={{ fontWeight: 600 }}>Disponible (movimiento)</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(monto, "USD")}</span>
                  {!giroInstdValido ? (
                    <>
                      <span style={{ fontWeight: 700 }}>Cuadre</span>
                      <span style={{ textAlign: "right", fontWeight: 700, color: "#c0392b" }}>
                        Cargá el monto instruido
                      </span>
                    </>
                  ) : giroModoParcial ? (
                    <>
                      <span style={{ fontWeight: 700 }}>Cuadre</span>
                      <span style={{ textAlign: "right", fontWeight: 700, color: "#e67e22" }}>
                        cuadre parcial — falta monto recibido
                      </span>
                    </>
                  ) : (
                    <>
                      <span>(a) Instruido − Citibank = Recibido</span>
                      <span
                        style={{ textAlign: "right", fontWeight: 700, color: giroCuadreAOk ? "#2e7d32" : "#c0392b" }}
                      >
                        {giroCuadreAOk ? "✓" : `✗ ${formatAmount(giroCuadreA, "USD")}`}
                      </span>
                      <span>(b) Cascada = Disponible</span>
                      <span
                        style={{ textAlign: "right", fontWeight: 700, color: giroCuadreBOk ? "#2e7d32" : "#c0392b" }}
                      >
                        {giroCuadreBOk ? "✓" : `✗ ${formatAmount(giroCuadreB, "USD")}`}
                      </span>
                      <span style={{ fontWeight: 700 }}>Cuadre</span>
                      <span
                        style={{ textAlign: "right", fontWeight: 700, color: giroCuadreReal ? "#2e7d32" : "#c0392b" }}
                      >
                        {giroCuadreReal ? "✓ cuadra (real)" : "✗ no cuadra"}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Transferencia internacional recibida (no vinculada aún): comisión de recepción
                neteada por Santander. Vincular acá la saca de "Por pagar" sin generar débito.
                Wizard export: Paso 3 (opcional). Para transferencias no-export, sin wizard. */}
            {esTransferenciaRecibida && (!esCobroExportacion || pasoExport === 3) && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  background: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                }}
              >
                <ComisionNeteadaSelector
                  key={`ingreso-${movimiento.id}`}
                  titulo="Comisión por recepción (neteada)"
                  cargarVinculadas={() => getComisionesIngresoNeteadas(movimiento.id)}
                  cargarDisponibles={() => getFacturasComisionIngresoDisponibles(pais)}
                  vincular={(facturaId) => vincularComisionIngreso(movimiento.id, facturaId)}
                  desvincular={(filaId) => desvincularComisionIngreso(filaId)}
                  destacar={(f) =>
                    !!f.fecha_emision &&
                    !!movimiento?.fecha &&
                    Math.abs(new Date(f.fecha_emision) - new Date(movimiento.fecha)) / 86400000 <= 3
                  }
                  formatAmount={formatAmount}
                  formatFecha={(s) => (s ? `${s.substring(8, 10)}/${s.substring(5, 7)}` : "—")}
                  moneda={pais === "Perú" ? "PEN" : "CLP"}
                  onCambio={onRefreshFuturos}
                  mostrarDteAnioAnterior={mostrarDteAnioAnterior}
                  setMostrarDteAnioAnterior={setMostrarDteAnioAnterior}
                />
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          {yaEstaVinculado || (esFactoring && yaVinculadoFactoring) ? "Cerrar" : "Cancelar"}
        </Button>
        {esCobroExportacion && !yaEstaVinculado ? (
          // Wizard export: Atrás / Siguiente entre los 3 pasos; Confirmar solo en el Paso 3.
          // El gate de habilitación (confirmarDisabled) es el mismo de siempre: exige facturas
          // seleccionadas + cuadre de costos OK (la comisión Santander del Paso 3 es opcional).
          <>
            {pasoExport > 1 && (
              <Button variant="outline-secondary" size="sm" onClick={() => setPasoExport((p) => p - 1)}>
                ← Atrás
              </Button>
            )}
            {pasoExport < 3 && (
              <Button variant="primary" size="sm" onClick={() => setPasoExport((p) => p + 1)}>
                Siguiente →
              </Button>
            )}
            {pasoExport === 3 && (
              <Button variant="primary" size="sm" onClick={handleConfirmar} disabled={confirmarDisabled}>
                {loading ? "Guardando..." : "Confirmar vinculación"}
              </Button>
            )}
          </>
        ) : (
          !(esFactoring && yaVinculadoFactoring) &&
          !yaEstaVinculado && (
            <Button variant="primary" size="sm" onClick={handleConfirmar} disabled={confirmarDisabled}>
              {loading ? "Guardando..." : esPrestamo ? "Vincular a cuota" : "Confirmar vinculación"}
            </Button>
          )
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default VincularMovimientoModal;
